import { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback, useId, useDeferredValue, memo } from 'react';
import { Link } from 'react-router-dom';
import { supplierReconAccountingAPI } from '../lib/api';
import {
  FiLoader, FiRefreshCw, FiDownload, FiCheck, FiChevronDown, FiChevronRight, FiAlertCircle,
  FiAlertTriangle, FiCpu, FiX, FiCheckCircle, FiCircle, FiInfo, FiZap, FiTrash2, FiInbox,
  FiLink, FiMoreHorizontal, FiArrowDown, FiBookOpen, FiEye, FiFileText, FiRotateCcw, FiEdit2,
} from 'react-icons/fi';

/**
 * Accounting tab for a Supplier Recon session.
 * Links every counted transaction (purchase-listing line or invoice) of an accepted supplier to an account
 * from the group's Set of Accounts. Fetches its own data; never touches the Report state.
 *
 * Mounting: the parent keeps this component mounted (hidden) after the tab was first opened, so filters, selection,
 * scroll position, loaded data and a running AI job survive tab switches. `active` tells it whether it is visible:
 * nothing is fetched while hidden except on an explicit refresh, and returning to the tab silently revalidates
 * (previous data stays on screen). A true unmount (navigating away) cancels a running AI job.
 *
 * Colour scheme (house rule: never white text on green): primary = white on blue-700 (6.7:1), status chips use
 * *-100 backgrounds with *-900/800 text, progress fills carry no text. Placeholders are gray-500 on white (4.8:1).
 */

// ─── Shared class constants (kept inside this file so the component stays self-contained) ─────────────────────
const FOCUS_RING = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-offset-1';
const BTN_BASE = `inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors motion-reduce:transition-none min-h-[44px] md:min-h-[32px] disabled:opacity-50 disabled:cursor-not-allowed ${FOCUS_RING}`;
const BTN_PRIMARY = `${BTN_BASE} bg-blue-700 text-white hover:bg-blue-800`;
// Outline borders are non-text UI boundaries: gray-500 / red-500 give >= 3:1 against white (WCAG 1.4.11).
const BTN_SECONDARY = `${BTN_BASE} border border-gray-500 text-gray-900 bg-white hover:bg-gray-50`;
const BTN_DANGER = `${BTN_BASE} border border-red-500 text-red-800 bg-white hover:bg-red-50`;
const BTN_DANGER_SOLID = `${BTN_BASE} bg-red-700 text-white hover:bg-red-800`;
// Neutral / cautionary confirm (never white on green): dark amber text on a light amber fill with an amber border.
const BTN_AMBER = `${BTN_BASE} border border-amber-700 bg-amber-100 text-amber-900 hover:bg-amber-200`;
const BTN_LINK = `inline-flex items-center gap-1 text-xs font-medium text-blue-800 underline hover:text-blue-900 min-h-[44px] md:min-h-0 ${FOCUS_RING}`;
const FIELD_BASE = 'px-2 py-1.5 text-xs border border-gray-500 rounded bg-white text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-700 min-h-[44px] md:min-h-[32px] disabled:bg-gray-50 disabled:text-gray-600';
const INPUT_CLS = `w-full ${FIELD_BASE}`;
const SELECT_CLS = FIELD_BASE;
// Native selects under 16px make iOS Safari zoom the page on focus: 16px (text-base) below sm, compact from sm up.
const SELECT_VAT = `${FIELD_BASE} text-base sm:text-xs`;
const CHECK_CLS = `w-4 h-4 ${FOCUS_RING}`;
const CHECK_LABEL = 'inline-flex items-center gap-2 text-xs text-gray-900 min-h-[44px] md:min-h-0';
const ICON_BTN = `inline-flex items-center justify-center min-w-[44px] min-h-[44px] rounded ${FOCUS_RING}`;
const CHIP = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap';
// Same chip but allowed to wrap (long text must never be cut off at 375px).
const CHIP_WRAP = CHIP.replace('whitespace-nowrap', 'whitespace-normal text-left');
const CARD = 'bg-white border border-gray-200 rounded-xl';
const SPIN = 'animate-spin motion-reduce:animate-none';
const SR_ONLY = 'sr-only';

const MAX_OPTIONS = 100;       // options rendered in one combobox list
const KEY_BATCH = 500;         // keys per API request (server max)
const ROW_PAGE = 50;           // rows rendered per supplier before "Show 50 more"
const EXPAND_ROW_CAP = 300;    // rows we are willing to render when expanding many suppliers at once
const NOTICE_MS = 6000;        // success notices auto-dismiss (never while hidden, hovered/focused, or when they carry an action)
const REASON_TOGGLE_MIN = 40;  // reasons longer than this get a more/less toggle (clamped to one line from sm up)
const ANNOUNCE_DELAY_MS = 500; // "N accounts available" waits for typing to pause
const UNASSIGN_CONFIRM_OVER = 3;
const SAVE_TIMEOUT_MS = 30000; // per-row and bulk assign / confirm / unassign requests give up after this
const SAVE_CFG = { timeout: SAVE_TIMEOUT_MS };
// VAT types come from the server (taxTypes map); this is only the fallback label set if an older API answers without it.
const VAT_FALLBACK = { 3: '15% VAT', 6: 'No VAT' };
const VAT_UNAVAILABLE = 'VAT types need a database update';
const VAT_UNAVAILABLE_HELP = 'Ask your administrator to run the database update.';
const MAX_NAMES_SHOWN = 5;     // supplier names listed in the export dialog before "and N more"
/** The ONE way a VAT type is written anywhere in this tab: '15% VAT (type 3)'. */
function vatLabelFor(labels, type) {
  const l = (labels && labels[type]) || VAT_FALLBACK[type];
  return l ? `${l} (type ${type})` : `VAT type ${type}`;
}
/** Safe value for a CSS attribute selector. */
const attrVal = (v) => String(v).replace(/["\\]/g, '\\$&');
const LIST_MAX_PX = 240;      // option list max height (15rem) when there is room
const LIST_MIN_W = 256;        // option list min width (16rem)
const EDGE_PX = 8;             // keep option lists this far from the viewport edge
const MENU_W_PX = 288;         // "Redo accounting" menu width (18rem), capped to the viewport
const MENU_NEED_PX = 240;      // room the menu wants below the trigger before it flips upward
const MENU_MAX_PX = 360;       // menu max height; it scrolls beyond this
const DEFAULT_FILTERS = { unassignedOnly: false, needsReview: false, search: '', accountType: '' };

function fmt(amount, currency = 'ZAR') {
  if (amount == null) return '—';
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency }).format(amount);
}

const errMsg = (err, fallback) => err?.response?.data?.error || fallback;
const isTimeout = (err) => !!err && !err.response && (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT' || /timeout/i.test(String(err.message || '')));
/** True when nothing meaningful has focus (body, nothing, or an element that left the DOM). */
const focusLost = () => { const ae = document.activeElement; return !ae || ae === document.body || !ae.isConnected; };

/** Like errMsg, but also reads the JSON error out of a blob response (CSV export uses responseType 'blob'). */
async function errMsgAsync(err, fallback) {
  const d = err?.response?.data;
  if (typeof Blob !== 'undefined' && d instanceof Blob) {
    try {
      const j = JSON.parse(await d.text());
      if (j && j.error) return j.error;
    } catch { /* not JSON */ }
    return fallback;
  }
  return (d && d.error) || fallback;
}

/** File name from a Content-Disposition header: RFC 5987 filename* first, then filename=, else the fallback. */
function filenameFrom(cd, fallback) {
  const star = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(cd || '');
  if (star) { try { return decodeURIComponent(star[1].trim()); } catch { /* malformed escape: fall through to filename= */ } }
  const m = /filename\s*=\s*"?([^";]+)"?/i.exec(cd || '');
  return m ? m[1].trim() : fallback;
}

function chunkKeys(keys) {
  const out = [];
  for (let i = 0; i < keys.length; i += KEY_BATCH) out.push(keys.slice(i, i + KEY_BATCH));
  return out;
}

/** An assignment only counts when its account still exists in the chart. */
const isAssigned = (t, byId) => !!t.assignment && (!byId || byId.has(t.assignment.accountId));

function countOf(txns, byId) {
  const c = { total: txns.length, assigned: 0, confirmed: 0, suggested: 0, unassigned: 0 };
  for (const t of txns) {
    if (!isAssigned(t, byId)) c.unassigned++;
    else { c.assigned++; if (t.assignment.status === 'confirmed') c.confirmed++; else c.suggested++; }
  }
  return c;
}

const accountLabel = (a) => `${a.account_code} - ${a.description || ''}`;
const plural = (n, one, many) => `${n} ${n === 1 ? one : (many || `${one}s`)}`;
const txnName = (t) => t.ref || t.purchaseRef || t.description || 'transaction';

/** Small stable hash so ids derived from supplier names stay unique even when the sanitised slugs collide. */
function hashStr(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}
const panelIdFor = (supplierKey) => `acct-panel-${String(supplierKey).replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').slice(0, 24)}-${hashStr(String(supplierKey))}`;

/** Row filters / sort rank shared by the list and by the "will this row move after my edit?" prediction. */
function passesRowFilters(t, filters, byId) {
  const assigned = isAssigned(t, byId);
  if (filters.unassignedOnly && assigned) return false;
  if (filters.needsReview && !(assigned && t.assignment.status === 'suggested')) return false;
  if (filters.accountType && (!assigned || (t.assignment.accountType || '') !== filters.accountType)) return false;
  return true;
}
const rankOf = (t, byId) => (!isAssigned(t, byId) ? 0 : t.assignment.status === 'suggested' ? 1 : 2);

/**
 * True when `el` is inside the viewport AND clear of the sticky UI: the scroll margins of its row (top margin for the
 * sticky notices, bottom margin = bulk bar height) count as covered area (WCAG 2.4.11 focus not obscured).
 */
function inViewport(el) {
  if (!el || !el.getBoundingClientRect) return true;
  const r = el.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight || 0;
  let top = 0; let bottom = 0;
  const host = el.closest ? el.closest('[data-row-key]') : null;
  if (host && window.getComputedStyle) {
    const cs = window.getComputedStyle(host);
    top = parseFloat(cs.scrollMarginTop) || 0; bottom = parseFloat(cs.scrollMarginBottom) || 0;
  }
  return r.top >= top && r.bottom <= vh - bottom;
}

/**
 * Where an option list goes, from the room around its input (viewport px). Vertical: the preferred side is kept only when
 * the list fits there, otherwise the side with more room wins; max-height always fits the chosen side (the list scrolls).
 * Horizontal: width = max(input width, 16rem) capped to the viewport; the list is shifted left so it never crosses the
 * right edge and never the left edge.
 */
function computeListPlacement(rect, vw, vh, preferTop) {
  const above = Math.max(rect.top - EDGE_PX, 0);
  const below = Math.max(vh - rect.bottom - EDGE_PX, 0);
  const prefRoom = preferTop ? above : below;
  const up = prefRoom >= LIST_MAX_PX ? preferTop : above > below;
  const room = up ? above : below;
  const maxHeight = Math.max(Math.min(LIST_MAX_PX, Math.floor(room) - 4), 64);
  const width = vw > 0 ? Math.min(Math.max(rect.width || 0, LIST_MIN_W), vw - 2 * EDGE_PX) : Math.max(rect.width || 0, LIST_MIN_W);
  let left = 0;
  if (vw > 0) {
    if (rect.left + width > vw - EDGE_PX) left = vw - EDGE_PX - width - rect.left;
    if (rect.left + left < EDGE_PX) left = EDGE_PX - rect.left;
  }
  return { up, maxHeight, width: Math.round(width), left: Math.round(left) };
}

/**
 * Where the "Redo accounting" menu goes (viewport px). Horizontal: anchored to the trigger's right edge when the trigger
 * sits in the right half of the viewport, to its left edge otherwise, then clamped so the menu never crosses either
 * viewport edge (offset is relative to the trigger wrapper). Vertical: below unless there is too little room below AND
 * more room above; max-height always fits the chosen side and the menu scrolls.
 */
function computeMenuPlacement(rect, vw, vh) {
  const above = Math.max(rect.top - EDGE_PX, 0);
  const below = Math.max(vh - rect.bottom - EDGE_PX, 0);
  const up = below < MENU_NEED_PX && above > below;
  const room = up ? above : below;
  const maxHeight = Math.max(Math.min(MENU_MAX_PX, Math.floor(room) - 4), 120);
  const width = vw > 0 ? Math.min(MENU_W_PX, vw - 2 * EDGE_PX) : MENU_W_PX;
  const right = rect.right !== undefined ? rect.right : rect.left + (rect.width || 0);
  const rightHalf = vw > 0 && (rect.left + right) / 2 > vw / 2;
  let abs = rightHalf ? right - width : rect.left;
  if (vw > 0) abs = Math.max(EDGE_PX, Math.min(abs, vw - EDGE_PX - width));
  return { up, maxHeight, width: Math.round(width), left: Math.round(abs - rect.left), rightHalf };
}

// ─── Account combobox ────────────────────────────────────────────────────────

/**
 * Accessible combobox over the group's accounts.
 * accountIndex = [{ a, code, hay }] with lowercase strings precomputed once per accounts list.
 * The listbox is only mounted while open, so 1000 rows cost 1000 small inputs, not 1000 popups.
 *
 * placement: 'bottom' (default) opens below the input, 'top' opens above it (bottom-full). This is only a preference: on
 * open (and on window resize) the room above/below is measured for EVERY placement and the preferred side is kept only
 * when the list fits there, otherwise the side with more room wins; max-height always fits the chosen side and the list
 * scrolls. Horizontally the list is clamped inside the viewport (shifted left near the right edge). The list is
 * absolutely positioned inside a `relative` wrapper and NO ancestor in this file clips it.
 *
 * Keyboard safety: Enter never acts on a stale list (query typed but the deferred list not yet rendered) and never
 * assigns when nothing is highlighted (empty query and no current account => no highlight until an arrow key/typing).
 */
const AccountCombobox = memo(function AccountCombobox({
  accountIndex, value, onSelect, onClear, disabled, label, announce, rowKey,
  placeholder = 'Search accounts…', placement = 'bottom', clearLabel = 'Clear account',
}) {
  const uid = useId();
  const listId = `${uid}-list`;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [place, setPlace] = useState({ up: placement === 'top', maxHeight: LIST_MAX_PX, width: 0, left: 0 });
  const [picked, setPicked] = useState({ sig: null, idx: -1 }); // highlight set by arrows / mouse, valid for one query only
  const deferredQuery = useDeferredValue(query);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const itemsRef = useRef([]);
  const activeRef = useRef(-1);
  const queryRef = useRef('');
  const deferredRef = useRef('');
  const lastAnnRef = useRef('');
  const ptrRef = useRef({ x: null, y: null }); // last real pointer position over the list (synthetic moves after scrolling are ignored)
  const valueId = value && !value.__removed ? value.id : null;
  const valueLabel = value ? accountLabel(value) : '';

  const filtered = useMemo(() => {
    if (!open) return { list: [], total: 0 };
    const q = deferredQuery.trim().toLowerCase();
    if (!q) {
      let list = accountIndex.slice(0, MAX_OPTIONS).map(x => x.a);
      // keep the current account reachable even when it is beyond the first page of options
      if (valueId && !list.some(a => a.id === valueId)) {
        const cur = accountIndex.find(x => x.a.id === valueId);
        if (cur) list = [cur.a, ...list.slice(0, MAX_OPTIONS - 1)];
      }
      return { list, total: accountIndex.length };
    }
    const tokens = q.split(/\s+/).filter(Boolean);
    const first = tokens[0];
    const scored = [];
    for (let i = 0; i < accountIndex.length; i++) {
      const x = accountIndex[i];
      if (!tokens.every(tk => x.hay.includes(tk))) continue;
      // rank: code prefix (whole query) > code prefix (first word) > code contains > anywhere
      const score = x.code.startsWith(q) ? 0 : x.code.startsWith(first) ? 1 : x.code.includes(first) ? 2 : 3;
      scored.push([score, i, x.a]);
    }
    scored.sort((p, r) => p[0] - r[0] || p[1] - r[1]);
    return { list: scored.slice(0, MAX_OPTIONS).map(s => s[2]), total: scored.length };
  }, [open, deferredQuery, accountIndex, valueId]);

  // The clear/delete option is only offered when the query is empty and it is placed LAST, so Enter after typing can
  // never clear by accident and the first real match is always the default highlight.
  const q = deferredQuery.trim();
  const items = useMemo(() => {
    const base = filtered.list.map(a => ({ a }));
    if (value && !q) base.push({ __clear: true });
    return base;
  }, [filtered.list, value, q]);

  // Default highlight is derived synchronously from the rendered list (no effect lag):
  //   typed query            -> first real match
  //   empty query + current  -> the current account
  //   empty query, no value  -> nothing (-1): Enter does nothing until an arrow key / typing sets a highlight
  const defaultActive = useMemo(() => {
    if (!open || items.length === 0) return -1;
    if (q) return 0;
    return valueId ? items.findIndex(it => it.a && it.a.id === valueId) : -1;
  }, [open, items, q, valueId]);
  const active = picked.sig === q && picked.idx >= 0 && picked.idx < items.length ? picked.idx : defaultActive;

  useLayoutEffect(() => {
    itemsRef.current = items;
    activeRef.current = active;
    deferredRef.current = deferredQuery;
  });

  useEffect(() => {
    if (!open || !listRef.current || active < 0) return;
    const el = listRef.current.querySelector(`[id="${uid}-opt-${active}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [active, open, uid]);

  // Announce the result count only after typing pauses, and only when the message actually changed.
  useEffect(() => {
    if (!open) { lastAnnRef.current = ''; return undefined; }
    if (!announce) return undefined;
    const msg = filtered.total === 0 ? 'No matching accounts' : `${plural(filtered.total, 'account')} available`;
    if (msg === lastAnnRef.current) return undefined;
    const id = setTimeout(() => { lastAnnRef.current = msg; announce(msg); }, ANNOUNCE_DELAY_MS);
    return () => clearTimeout(id);
  }, [open, filtered.total, announce]);

  const updateQuery = (v) => { queryRef.current = v; setQuery(v); };
  const measure = () => {
    if (!inputRef.current) return null;
    const vw = window.innerWidth || document.documentElement.clientWidth || 0;
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    return computeListPlacement(inputRef.current.getBoundingClientRect(), vw, vh, placement === 'top');
  };
  const applyPlacement = () => { const p = measure(); if (p) setPlace(p); };
  const openList = (initialQuery = '') => {
    updateQuery(initialQuery);
    setPicked({ sig: null, idx: -1 });
    ptrRef.current = { x: null, y: null };
    applyPlacement();
    setOpen(true);
  };

  // Rotating / resizing while open re-evaluates the room (the list stays inside the viewport)
  useEffect(() => {
    if (!open) return undefined;
    const onResize = () => { const p = measure(); if (p) setPlace(p); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open, placement]); // eslint-disable-line react-hooks/exhaustive-deps
  const close = () => { setOpen(false); updateQuery(''); setPicked({ sig: null, idx: -1 }); };
  const choose = (item) => {
    if (!item) return;
    close();
    if (item.__clear) onClear(); else if (!valueId || item.a.id !== valueId) onSelect(item.a);
  };
  const highlight = (idx) => setPicked({ sig: deferredRef.current.trim(), idx });
  const move = (delta) => {
    const n = itemsRef.current.length;
    if (n === 0) return;
    const cur = activeRef.current;
    ptrRef.current = { x: null, y: null }; // the list will scroll under a still pointer: its synthetic mousemove must not steal the highlight
    highlight(cur < 0 ? (delta > 0 ? 0 : n - 1) : Math.min(Math.max(cur + delta, 0), n - 1));
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    const k = e.key;
    // IME composition: Enter confirms the composition and arrows pick candidates - never act on them (open or closed)
    if ((k === 'Enter' || k === 'ArrowUp' || k === 'ArrowDown') && (e.nativeEvent?.isComposing || e.keyCode === 229)) return;
    if (!open) {
      // Closed: the box shows the account label, so typing must START a fresh query (never append to the label).
      if (k === 'ArrowDown' || k === 'ArrowUp') { e.preventDefault(); openList(''); }
      else if (k === 'Backspace' || k === 'Delete' || k === ' ') { e.preventDefault(); openList(''); }
      else if (k.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) { e.preventDefault(); openList(k); }
      return;
    }
    if (k === 'ArrowDown') { e.preventDefault(); move(1); }
    else if (k === 'ArrowUp') { e.preventDefault(); move(-1); }
    else if (k === 'PageDown') { e.preventDefault(); move(10); }
    else if (k === 'PageUp') { e.preventDefault(); move(-10); }
    else if ((k === 'Home' || k === 'End') && !queryRef.current) {
      // Home/End jump within the list only while the text box is empty (otherwise they move the text cursor)
      e.preventDefault();
      if (itemsRef.current.length) highlight(k === 'Home' ? 0 : itemsRef.current.length - 1);
    } else if (k === 'Enter') {
      e.preventDefault();
      // Never act on a stale list: the query was typed but the (deferred) list for it has not been rendered yet.
      if (queryRef.current.trim() !== deferredRef.current.trim()) return;
      const idx = activeRef.current;
      if (idx >= 0) choose(itemsRef.current[idx]);
    } else if (k === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
    else if (k === 'Tab') close();
  };

  const onChange = (e) => {
    let v = e.target.value;
    if (!open) {
      // IME / virtual keyboards deliver the edit here instead of through keydown; strip the label we were showing.
      if (valueLabel) {
        if (v.startsWith(valueLabel)) v = v.slice(valueLabel.length);
        else if (valueLabel.startsWith(v)) v = '';
      }
      setPicked({ sig: null, idx: -1 });
      ptrRef.current = { x: null, y: null };
      applyPlacement();
      setOpen(true);
    }
    updateQuery(v);
  };

  const onPaste = (e) => {
    if (open || disabled) return;
    e.preventDefault();
    const text = (e.clipboardData && e.clipboardData.getData('text')) || '';
    openList(text.replace(/\s+/g, ' ').slice(0, 200));
  };

  const placeTop = place.up;
  const shifted = place.left !== 0;

  return (
    <div className="relative w-full">
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={open && active >= 0 && items[active] ? `${uid}-opt-${active}` : undefined}
        data-combobox-key={rowKey}
        autoComplete="off"
        disabled={disabled}
        placeholder={open && value ? valueLabel : (value ? undefined : placeholder)}
        className={INPUT_CLS}
        value={open ? query : valueLabel}
        title={value ? valueLabel : undefined}
        onChange={onChange}
        onPaste={onPaste}
        onClick={() => { if (!disabled && !open) openList(''); }}
        onBlur={close}
        onKeyDown={onKeyDown}
      />
      {open && (
        <ul
          id={listId}
          ref={listRef}
          role="listbox"
          aria-label={`${label} options`}
          data-placement={placeTop ? 'top' : 'bottom'}
          data-align={shifted ? 'shifted' : 'left'}
          // keep focus in the input while the pointer is on the list (scrollbar drag must not close it)
          onMouseDown={(e) => e.preventDefault()}
          style={{ maxHeight: `${place.maxHeight}px`, left: `${place.left}px`, ...(place.width ? { width: `${place.width}px` } : {}) }}
          className={`absolute z-[24] w-full min-w-[16rem] max-w-[calc(100vw-1rem)] overflow-auto bg-white border border-gray-400 rounded-lg shadow-lg text-xs ${placeTop ? 'bottom-full mb-1' : 'mt-1'}`}
        >
          {items.length === 0 && (
            <li role="presentation" className="px-3 py-2 text-gray-700">No matching accounts</li>
          )}
          {items.map((item, idx) => {
            const isActive = idx === active;
            const isCurrent = !item.__clear && item.a.id === valueId;
            return (
              <li
                key={item.__clear ? '__clear' : item.a.id}
                id={`${uid}-opt-${idx}`}
                role="option"
                aria-selected={isCurrent}
                onMouseDown={(e) => { e.preventDefault(); choose(item); }}
                onMouseMove={(e) => {
                  // only a pointer that really moved may change the highlight (scrollIntoView makes browsers fire synthetic moves)
                  const p = ptrRef.current; const moved = p.x !== null && (p.x !== e.clientX || p.y !== e.clientY);
                  ptrRef.current = { x: e.clientX, y: e.clientY };
                  if (moved && !isActive) highlight(idx);
                }}
                className={`px-3 py-2 cursor-pointer min-h-[44px] md:min-h-0 flex items-center gap-2 ${isActive ? 'bg-blue-100 text-blue-900' : 'text-gray-900'} ${item.__clear ? 'italic text-gray-800 border-t border-gray-200' : ''} ${isCurrent ? 'font-semibold' : ''}`}
              >
                {item.__clear ? (
                  clearLabel
                ) : (
                  <>
                    {isCurrent && <FiCheck className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />}
                    <span className="flex-1 min-w-0 break-words">{accountLabel(item.a)}</span>
                    {item.a.account_type && (
                      <span className="flex-shrink-0 px-1.5 py-0.5 rounded bg-gray-100 text-gray-800 border border-gray-300 text-[11px]">{item.a.account_type}</span>
                    )}
                  </>
                )}
              </li>
            );
          })}
          {filtered.total > MAX_OPTIONS && (
            <li role="presentation" className="px-3 py-2 text-gray-700 border-t border-gray-100">
              Showing first {MAX_OPTIONS} of {filtered.total}. Keep typing to narrow the list.
            </li>
          )}
        </ul>
      )}
    </div>
  );
});

// ─── Chips ───────────────────────────────────────────────────────────────────

function StatusChip({ assignment, accountMissing }) {
  if (!assignment || accountMissing) {
    return (
      <span className={`${CHIP} bg-gray-100 text-gray-800`}>
        <FiCircle className="w-3 h-3" aria-hidden="true" /> Unassigned
      </span>
    );
  }
  if (assignment.status === 'confirmed') {
    return (
      <span className={`${CHIP} bg-green-100 text-green-900`}>
        <FiCheckCircle className="w-3 h-3" aria-hidden="true" /> Confirmed
      </span>
    );
  }
  if (assignment.source === 'manual') {
    // Set by hand, then un-confirmed ("Redo accounting"): it is still the user's own pick, so it is not an AI suggestion.
    return (
      <span className={`${CHIP_WRAP} bg-blue-50 text-blue-900 border border-blue-200`} data-chip="manual-review">
        <FiEdit2 className="w-3 h-3" aria-hidden="true" /> To review <span className="font-normal">(set by hand)</span>
      </span>
    );
  }
  return (
    <span className={`${CHIP} bg-amber-100 text-amber-900`}>
      <FiCpu className="w-3 h-3" aria-hidden="true" /> {assignment.source === 'rule' ? 'Suggested (default)' : 'AI suggested'}
    </span>
  );
}

/** Payment decision made in the Report (read-only here). Prefixed "Report:" so it is not mistaken for a status. */
function PaymentChip({ status }) {
  if (!status) return null;
  const map = {
    accepted: ['payment accepted', 'bg-green-100 text-green-900'],
    custom: ['custom payment', 'bg-blue-100 text-blue-900'],
    rejected: ['payment rejected', 'bg-red-100 text-red-900'],
  };
  const [text, cls] = map[status] || [`payment ${status}`, 'bg-gray-100 text-gray-800'];
  return <span className={`${CHIP} ${cls}`}>Report: {text}</span>;
}

// ─── Accessible confirm dialog ───────────────────────────────────────────────

/**
 * Focus trap that survives lost focus: Escape and Tab are handled by a document-level listener while the dialog is
 * open (so they work even if focus ended up on <body>), focus that escapes the panel is pulled back, and a click on
 * the backdrop keeps focus inside. On close, focus returns to the opener; when the opener is gone or disabled
 * (typical: the action it started is running) it falls back to `fallbackRef` (the tab heading).
 */
function ConfirmDialog({ dlg, onClose, fallbackRef }) {
  const uid = useId();
  const cancelRef = useRef(null);
  const panelRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const prev = document.activeElement;
    if (cancelRef.current) cancelRef.current.focus();
    const focusables = () => (panelRef.current ? [...panelRef.current.querySelectorAll('button:not([disabled])')] : []);
    const onDocKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onCloseRef.current(); return; }
      if (e.key !== 'Tab') return;
      const f = focusables();
      if (f.length === 0) return;
      const cur = document.activeElement;
      const inside = !!(panelRef.current && cur && panelRef.current.contains(cur));
      if (!inside) { e.preventDefault(); (e.shiftKey ? f[f.length - 1] : f[0]).focus(); return; }
      if (e.shiftKey && cur === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && cur === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
    };
    const onFocusIn = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) && cancelRef.current) cancelRef.current.focus();
    };
    document.addEventListener('keydown', onDocKey, true);
    document.addEventListener('focusin', onFocusIn, true);
    return () => {
      document.removeEventListener('keydown', onDocKey, true);
      document.removeEventListener('focusin', onFocusIn, true);
      // `prev === document.body` means the dialog was opened with focus already lost: treat it like a missing opener
      if (prev && prev !== document.body && prev.focus && prev.isConnected && !prev.disabled) prev.focus();
      else if (fallbackRef && fallbackRef.current) fallbackRef.current.focus({ preventScroll: true });
    };
  }, [fallbackRef]);

  const keepFocus = (e) => {
    if (e.target === e.currentTarget) { e.preventDefault(); if (cancelRef.current) cancelRef.current.focus(); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4" onMouseDown={keepFocus} data-dialog-backdrop="">
      <div
        ref={panelRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={`${uid}-t`}
        aria-describedby={`${uid}-d`}
        tabIndex={-1}
        className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-3 max-h-[90vh] overflow-auto focus:outline-none"
      >
        <h2 id={`${uid}-t`} className="text-base font-semibold text-gray-900">{dlg.title}</h2>
        <div id={`${uid}-d`} className="text-sm text-gray-800 space-y-2">{dlg.body}</div>
        <div className="flex justify-end gap-2 flex-wrap pt-1">
          <button ref={cancelRef} type="button" className={BTN_SECONDARY} onClick={onClose}>Cancel</button>
          <button type="button" className={dlg.danger ? BTN_DANGER_SOLID : dlg.amber ? BTN_AMBER : BTN_PRIMARY} onClick={() => { onClose(); dlg.onConfirm(); }}>
            {dlg.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── "Redo accounting" menu button ──────────────────────────────────────────

/**
 * Accessible menu button (WAI-ARIA menu button pattern): aria-haspopup/aria-expanded on the trigger, arrow keys / Home / End
 * inside the menu, Escape closes and returns focus to the trigger, Tab and an outside click close it. Disabled items stay
 * focusable (aria-disabled) and show their reason as visible text. Choosing an item closes the menu and puts focus back on
 * the trigger BEFORE the confirm dialog opens, so the dialog restores focus to the trigger when it closes.
 * items: [{ id, label, hint, disabledReason, danger, onSelect }]
 *
 * Redo works on accepted AND non-accepted recons alike (it only changes the Accounting tab), so there is intentionally no
 * "recon accepted" blocking or disabled reason here; the hint below tells people to use Reopen recon to change the recon.
 */
const REDO_SCOPE_HINT = 'Redo only the Accounting tab. To change the recon itself use Reopen recon.';
function RedoMenu({ items, busy, triggerRef }) {
  const uid = useId();
  const menuId = `${uid}-menu`;
  const hintId = `${uid}-hint`;
  const [open, setOpen] = useState(false);
  const [focusIdx, setFocusIdx] = useState(0);
  const [place, setPlace] = useState({ up: false, maxHeight: MENU_MAX_PX, width: MENU_W_PX, left: 0, rightHalf: false });
  const wrapRef = useRef(null);
  const itemRefs = useRef([]);

  const measure = () => {
    if (!triggerRef.current) return null;
    const vw = window.innerWidth || document.documentElement.clientWidth || 0;
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    return computeMenuPlacement(triggerRef.current.getBoundingClientRect(), vw, vh);
  };
  useEffect(() => { if (open && itemRefs.current[focusIdx]) itemRefs.current[focusIdx].focus({ preventScroll: true }); }, [open, focusIdx]);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onResize = () => { const p = measure(); if (p) setPlace(p); };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('touchstart', onDown, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDown, true); document.removeEventListener('touchstart', onDown, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const openAt = (i) => { const p = measure(); if (p) setPlace(p); setFocusIdx(i); setOpen(true); };
  const onTriggerKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); openAt(0); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); openAt(items.length - 1); }
  };
  const onWrapKey = (e) => {
    if (!open) return;
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setOpen(false); if (triggerRef.current) triggerRef.current.focus(); return; }
    // Tab: park focus on the trigger FIRST (the menu is about to unmount, which would drop focus to <body>), then close
    // WITHOUT preventDefault so the browser moves on from the trigger to the next / previous control.
    if (e.key === 'Tab') { if (triggerRef.current) triggerRef.current.focus(); setOpen(false); return; }
    if (e.target === triggerRef.current) return;
    const last = items.length - 1;
    if (e.key === 'ArrowDown') { e.preventDefault(); setFocusIdx(i => (i >= last ? 0 : i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setFocusIdx(i => (i <= 0 ? last : i - 1)); }
    else if (e.key === 'Home') { e.preventDefault(); setFocusIdx(0); }
    else if (e.key === 'End') { e.preventDefault(); setFocusIdx(last); }
  };

  return (
    <div ref={wrapRef} className="relative w-full sm:w-auto sm:inline-block" onKeyDown={onWrapKey}>
      <button ref={triggerRef} type="button" className={`${BTN_SECONDARY} w-full sm:w-auto`} data-redo-btn="" aria-haspopup="menu" aria-expanded={open}
        aria-controls={open ? menuId : undefined} aria-busy={busy || undefined}
        title={REDO_SCOPE_HINT}
        onClick={() => { if (open) setOpen(false); else openAt(0); }} onKeyDown={onTriggerKey}>
        <FiRotateCcw className="w-3.5 h-3.5" aria-hidden="true" /> Redo accounting <FiChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
      </button>
      {open && (
        <div data-redo-popup="" data-placement={`${place.up ? 'top' : 'bottom'}-${place.rightHalf ? 'right' : 'left'}`}
          style={{ left: place.left, width: place.width, maxHeight: place.maxHeight }}
          className={`absolute z-30 ${place.up ? 'bottom-full mb-1' : 'top-full mt-1'} flex flex-col bg-white border border-gray-500 rounded-lg shadow-lg overflow-hidden`}>
          <p id={hintId} className="px-3 pt-2 pb-1 text-xs text-gray-800" data-redo-hint="">{REDO_SCOPE_HINT}</p>
          <div id={menuId} role="menu" aria-label="Redo accounting" aria-describedby={hintId} className="p-1 overflow-y-auto min-h-0">
            {items.map((it, i) => {
              const reasonId = `${uid}-r${i}`;
              const disabled = !!it.disabledReason;
              return (
                <div key={it.id} role="none">
                  {i > 0 && it.danger && <div role="separator" className="my-1 border-t border-gray-300" />}
                  <button ref={(el) => { itemRefs.current[i] = el; }} type="button" role="menuitem" tabIndex={-1}
                    data-redo-item={it.id} aria-disabled={disabled || undefined} aria-describedby={reasonId}
                    className={`w-full text-left rounded-md px-3 py-2 min-h-[44px] ${disabled ? 'cursor-not-allowed text-gray-600' : it.danger ? 'text-red-800 hover:bg-red-50' : 'text-gray-900 hover:bg-gray-50'} ${FOCUS_RING}`}
                    onClick={() => {
                      if (disabled) return;
                      setOpen(false);
                      if (triggerRef.current) triggerRef.current.focus();
                      it.onSelect();
                    }}>
                    <span className="block text-sm font-medium">{it.label}</span>
                    <span id={reasonId} className="block text-xs text-gray-700">{disabled ? it.disabledReason : it.hint}</span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Transaction row (memoised; callbacks are stable and take keys, not closures) ────────────────────────────

const TxnRow = memo(function TxnRow({
  t, accountIndex, accountsById, currency, readOnly, selected, pending, rowError,
  onToggleSelect, onPick, onClear, onAccept, onDismissRowError, announce,
  vatEnabled, vatLabels, supplierVat, onSetVat,
}) {
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reasonClamped, setReasonClamped] = useState(false);
  const reasonRef = useRef(null);
  const a = t.assignment;
  const acct = a ? accountsById.get(a.accountId) : null;
  const removedCode = a && !acct ? a.accountCode : (!a && t.removedAccount ? t.removedAccount.accountCode : null);
  const accountMissing = !!(a && !acct);
  const hasRemoved = !acct && (a || t.removedAccount);
  // Fallback so the combobox never shows an empty box for an assignment whose account left the chart
  const comboValue = acct || (hasRemoved
    ? { id: '__removed', __removed: true, account_code: removedCode || '?', description: (a && a.accountDescription) || 'no longer in chart' }
    : null);
  const name = txnName(t);
  const reason = a && a.source !== 'manual' && (a.status === 'suggested' || a.reason) ? (a.reason || 'No reason given') : null;
  const showConf = a && !accountMissing && a.status === 'suggested' && a.source === 'ai' && a.confidence != null;
  // VAT type: a row override can only be stored on an assigned row (it lives on the assignment); other rows follow the supplier default.
  const vatOverride = t.taxTypeSource === 'row';
  const vatLabel = vatLabelFor(vatLabels, t.taxType);
  const [vatEdit, setVatEdit] = useState(false);
  const vatSelRef = useRef(null);
  const canSetVat = vatEnabled && !readOnly && !!a && !accountMissing;

  // The reason is clamped to one line from sm up (never below sm). A toggle is offered for any reason that could be
  // clamped: long text, or text the browser actually truncated.
  // Re-measured whenever the element is resized (rotation, window resize, breakpoint change); falls back to a window
  // resize listener without ResizeObserver. Both are removed on unmount.
  useLayoutEffect(() => {
    const el = reasonRef.current;
    if (!el || reasonOpen) return undefined;
    const measure = () => setReasonClamped(el.scrollHeight > el.clientHeight + 1);
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [reason, reasonOpen]);
  const canToggleReason = !!reason && (reason.length > REASON_TOGGLE_MIN || reasonClamped);
  // Tapping "Change" (phones) reveals the select and moves focus into it.
  useEffect(() => { if (vatEdit && vatSelRef.current) vatSelRef.current.focus(); }, [vatEdit]);

  return (
    <li
      className="py-3 space-y-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-inset rounded scroll-mt-56 sm:scroll-mt-28"
      style={{ scrollMarginBottom: 'var(--acct-bar-h, 6rem)' }}
      tabIndex={-1}
      data-txn-key={t.key}
      data-row-key={t.key}
    >
      <div className="flex items-start gap-2">
        {!readOnly && (
          <label className="flex items-center justify-center min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0 flex-shrink-0">
            <input type="checkbox" className={CHECK_CLS} checked={selected}
              aria-label={`Select ${name} of ${fmt(t.amount, currency)}`}
              onChange={() => onToggleSelect(t.key)} />
          </label>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
              <span className="text-gray-700">{t.date || 'No date'}</span>
              <span className="font-medium text-gray-900 break-words">{t.ref || '—'}{t.purchaseRef ? ` / ${t.purchaseRef}` : ''}</span>
              <span className="px-1.5 py-0.5 rounded border border-gray-300 bg-gray-50 text-gray-800 text-[11px] font-medium">
                {t.kind === 'pl_line' ? 'PL line' : 'Invoice'}
              </span>
            </div>
            <span className="text-sm font-semibold text-gray-900 tabular-nums text-right whitespace-nowrap">{fmt(t.amount, currency)}</span>
          </div>
          {t.description && <p className="text-sm text-gray-700 break-words mt-0.5">{t.description}</p>}
        </div>
      </div>

      <div className={`flex flex-wrap items-center gap-2 ${readOnly ? '' : 'md:pl-0'}`}>
        <StatusChip assignment={a} accountMissing={accountMissing || (!a && !!t.removedAccount)} />
        {showConf && <span className="text-xs text-gray-800">{Math.round(a.confidence * 100)}% confident</span>}
        <div className="flex-1 min-w-[12rem]">
          {readOnly ? (
            <p className="text-xs text-gray-900 break-words">
              {comboValue ? accountLabel(comboValue) : <span className="text-gray-700">No account</span>}
            </p>
          ) : (
            <AccountCombobox
              accountIndex={accountIndex}
              value={comboValue}
              label={`Account for ${name} (${fmt(t.amount, currency)})`}
              rowKey={t.key}
              announce={announce}
              disabled={false}
              onSelect={(sel) => onPick(t.key, sel)}
              onClear={() => onClear(t.key)}
            />
          )}
        </div>
        {vatEnabled && (canSetVat ? (
          <div className="flex items-center gap-1.5 flex-wrap" data-vat-key={t.key}>
            {/* Below sm: compact text + a small Change button (no full-width select on every row); sm and up: the select inline. */}
            <span className="sm:hidden text-xs text-gray-800" data-vat-compact={t.key}>
              VAT: {vatLabel}{vatOverride ? '' : ' (supplier default)'}
            </span>
            {!vatEdit && (
              <button type="button" className={`sm:hidden ${BTN_LINK}`} data-vat-change={t.key} aria-label={`Change VAT type for ${name}`}
                onClick={() => setVatEdit(true)}>
                Change
              </button>
            )}
            <select ref={vatSelRef} aria-label={`VAT type for ${name}`} data-vat-select={t.key}
              className={`${SELECT_VAT} w-full sm:w-auto ${vatEdit ? '' : 'hidden sm:block'}`}
              value={vatOverride ? String(t.taxType) : ''} onChange={(e) => onSetVat(t.key, e.target.value)}
              onBlur={() => setVatEdit(false)}>
              <option value="">{`Supplier default (${vatLabelFor(vatLabels, supplierVat)})`}</option>
              {Object.keys(vatLabels).map((k) => <option key={k} value={k}>{vatLabelFor(vatLabels, k)}</option>)}
            </select>
            {vatOverride && <span className={`${CHIP} bg-blue-100 text-blue-900`}>VAT override</span>}
          </div>
        ) : (
          <span className="text-xs text-gray-800" data-vat-text={t.key}>
            VAT: {vatLabel}{vatOverride ? ' (row override)' : ''}
          </span>
        ))}
        {pending && <span className="text-xs text-gray-700 inline-flex items-center gap-1"><FiLoader className={`w-3 h-3 ${SPIN}`} aria-hidden="true" /> Saving…</span>}
        {!readOnly && a && !accountMissing && a.status === 'suggested' && (
          <button type="button" className={`${BTN_SECONDARY} !px-2`} data-accept-key={t.key}
            aria-label={`Accept suggestion for ${name}`} onClick={() => onAccept(t.key)}>
            <FiCheck className="w-3 h-3" aria-hidden="true" /> Accept
          </button>
        )}
      </div>

      {rowError && (
        <p role="alert" className="text-xs text-red-800 flex items-start gap-1" data-row-error={t.key}>
          <FiAlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" aria-hidden="true" />
          <span className="min-w-0 flex-1 break-words">{rowError}</span>
          <button type="button" aria-label={`Dismiss error for ${name}`} className={`${ICON_BTN} !min-w-[44px] md:!min-h-[24px] md:!min-w-[24px] text-red-800`}
            onClick={() => onDismissRowError(t.key)}><FiX className="w-3.5 h-3.5" aria-hidden="true" /></button>
        </p>
      )}

      {hasRemoved && (
        <p className="text-xs text-red-800 inline-flex items-start gap-1">
          <FiAlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" aria-hidden="true" />
          Account {removedCode || ''} is no longer in the chart, so this transaction counts as unassigned. {readOnly ? '' : 'Pick a new account.'}
        </p>
      )}

      {reason && !accountMissing && (
        <div className="text-xs text-gray-700 flex items-start gap-1">
          <span className="font-medium text-gray-900 flex-shrink-0">Why this account:</span>
          <span ref={reasonRef} className={reasonOpen ? 'min-w-0 flex-1 break-words' : 'min-w-0 flex-1 break-words sm:line-clamp-1'}>{reason}</span>
          {canToggleReason && (
            <button type="button"
              className={`hidden sm:inline-flex items-center justify-center flex-shrink-0 min-h-[44px] min-w-[44px] md:min-h-[24px] md:min-w-0 text-blue-800 underline ${FOCUS_RING}`}
              aria-label={reasonOpen ? 'Show less of the reason' : 'Show more of the reason'}
              onClick={() => setReasonOpen(o => !o)}>
              {reasonOpen ? 'less' : 'more'}
            </button>
          )}
        </div>
      )}
    </li>
  );
});

// ─── Supplier defaults panel (collapsible) ───────────────────────────────────

function DefaultsPanel({ groupId, accountIndex, accountsById, supplierNames, readOnly, open, reloadTick, onToggle, announce, onError, askConfirm, panelRef }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refocus, setRefocus] = useState(0);
  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  // After a Delete the row (and the button/combobox that had focus) is gone: hand focus to the first remaining row's
  // combobox, else the panel header button - but only when focus really was lost (never steal it from elsewhere).
  useEffect(() => {
    if (!refocus) return;
    const root = panelRef && panelRef.current;
    if (root && focusLost()) {
      const el = root.querySelector('input[data-combobox-key]') || root.querySelector('button');
      if (el) el.focus({ preventScroll: true });
    }
  }, [refocus]); // eslint-disable-line react-hooks/exhaustive-deps

  const reload = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    try {
      const res = await supplierReconAccountingAPI.listDefaults(groupId);
      if (mountedRef.current) setRows(res.data?.defaults || []);
    } catch (err) {
      if (mountedRef.current) onError(errMsg(err, 'Failed to load supplier defaults'));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [groupId, onError]);

  // Loaded when first opened, whenever reopened, and whenever the parent asks for a refresh (reloadTick) - e.g. after
  // "View supplier defaults" while the panel is already open.
  useEffect(() => { if (open) reload(); }, [open, reload, reloadTick]);

  const change = async (d, acct) => {
    try {
      // supplierKey (not the display name): editing must hit the SAME row, never re-resolve through aliases
      await supplierReconAccountingAPI.saveDefault(groupId, { supplierKey: d.supplier_key, cat: d.cat_key || '', accountId: acct.id });
      announce(`Default account for ${supplierNames.get(d.supplier_key) || d.supplier_key} changed to ${acct.account_code}`);
      await reload();
    } catch (err) { onError(errMsg(err, 'Could not change the default.')); }
  };

  const remove = (d) => askConfirm({
    title: 'Delete this default?',
    body: <p>Future AI matching will no longer pre-fill an account for {supplierNames.get(d.supplier_key) || d.supplier_key}{d.cat_key ? ` (${d.cat_key})` : ''}. Existing assignments are not changed.</p>,
    confirmLabel: 'Delete default',
    danger: true,
    onConfirm: async () => {
      try {
        await supplierReconAccountingAPI.deleteDefault(groupId, d.id);
        announce('Default deleted');
        await reload();
        if (mountedRef.current) setRefocus(n => n + 1);
      } catch (err) { onError(errMsg(err, 'Could not delete the default.')); }
    },
  });

  return (
    <section ref={panelRef} id="acct-defaults-panel" className={`${CARD} scroll-mt-32`} aria-label="Supplier defaults">
      <button type="button" aria-expanded={open} aria-controls={open ? 'acct-defaults-body' : undefined} onClick={onToggle}
        className={`w-full flex items-center gap-2 px-4 py-3 text-left min-h-[44px] rounded-xl ${FOCUS_RING}`}>
        {open ? <FiChevronDown className="w-4 h-4 text-gray-700" aria-hidden="true" /> : <FiChevronRight className="w-4 h-4 text-gray-700" aria-hidden="true" />}
        <FiBookOpen className="w-4 h-4 text-gray-700" aria-hidden="true" />
        <span className="text-sm font-semibold text-gray-900">Supplier defaults</span>
        {rows && <span className="text-xs text-gray-700">({rows.length})</span>}
      </button>
      {open && (
        <div id="acct-defaults-body" className="border-t border-gray-100 px-4 py-3 space-y-2">
          <p className="text-xs text-gray-700">
            Defaults pre-fill an account when AI matching runs. They apply to this recon group. A manual default is never replaced by a remembered pick.
          </p>
          {loading && !rows && <p className="text-xs text-gray-700 inline-flex items-center gap-1"><FiLoader className={`w-3.5 h-3.5 ${SPIN}`} aria-hidden="true" /> Loading defaults…</p>}
          {rows && rows.length === 0 && (
            <p className="text-xs text-gray-700">No supplier defaults yet. Switch on &quot;Remember my picks as supplier defaults&quot; and pick accounts to create some.</p>
          )}
          {rows && rows.length > 0 && (
            <ul className="divide-y divide-gray-100">
              {rows.map(d => {
                const acct = accountsById.get(d.account_id);
                const val = acct || (d.account_code ? { id: '__removed', __removed: true, account_code: d.account_code, description: 'no longer in chart' } : null);
                const sname = supplierNames.get(d.supplier_key) || d.supplier_key;
                return (
                  <li key={d.id} className="py-2 flex flex-col md:flex-row md:items-center gap-2">
                    <div className="md:w-1/3 min-w-0 text-xs">
                      <p className="font-medium text-gray-900 break-words">{sname}</p>
                      <p className="text-gray-700">{d.cat_key ? `Category: ${d.cat_key}` : 'Any category'}</p>
                    </div>
                    <div className="flex-1 min-w-0">
                      {readOnly ? (
                        <p className="text-xs text-gray-900 break-words">{val ? accountLabel(val) : 'Account removed'}</p>
                      ) : (
                        <AccountCombobox accountIndex={accountIndex} value={val} label={`Default account for ${sname}${d.cat_key ? ` ${d.cat_key}` : ''}`}
                          rowKey={`def-${d.id}`} announce={announce} placement="top" clearLabel="Delete default"
                          onSelect={(a) => change(d, a)} onClear={() => remove(d)} />
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-800">
                      <span>Used {d.usage_count || 0}x</span>
                      <span className={`${CHIP} ${d.source === 'manual' ? 'bg-blue-100 text-blue-900' : 'bg-gray-100 text-gray-800'}`}>{d.source}</span>
                      {!readOnly && (
                        <button type="button" className={`${BTN_DANGER} !px-2`} aria-label={`Delete default for ${sname}`} onClick={() => remove(d)}>
                          <FiTrash2 className="w-3.5 h-3.5" aria-hidden="true" /> Delete
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function SupplierReconAccounting({ sessionId, groupId, canEdit, active = true, onOpenMatches, onAiRunningChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);       // first load only (no data yet)
  const [refreshing, setRefreshing] = useState(false); // silent revalidation: previous data stays visible
  const [error, setError] = useState('');
  const [rowErrors, setRowErrors] = useState({});      // txnKey -> message (shown next to the row as well as in the banner)
  const [notice, setNotice] = useState(null);          // { text, action? }
  const [noticeHold, setNoticeHold] = useState(false); // pointer/focus is on the notice: do not auto-dismiss
  const [busy, setBusy] = useState(false);             // bulk / global actions only (never single-row saves)
  const [pendingKeys, setPendingKeys] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());
  const [shown, setShown] = useState({});              // supplierKey -> rows rendered
  const [selected, setSelected] = useState(() => new Set());
  const [bulkAccount, setBulkAccount] = useState(null);
  const [bulkLearn, setBulkLearn] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [learnRows, setLearnRows] = useState(false);
  const [applyState, setApplyState] = useState({});    // supplierKey -> { account, scope, learn }
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [unassignedFirst, setUnassignedFirst] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const [reviewBase, setReviewBase] = useState(0);     // suggestions to review when review mode started (for "reviewed X of Y")
  const [ai, setAi] = useState({ running: false, cancelling: false, done: 0, total: 0 });
  const [dlg, setDlg] = useState(null);
  const [announcement, setAnnouncement] = useState('');
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const [defaultsTick, setDefaultsTick] = useState(0);
  const [orphanOpen, setOrphanOpen] = useState(false);
  const [orphanDismissed, setOrphanDismissed] = useState('');
  const [pendingFocus, setPendingFocus] = useState(null);
  const [preparing, setPreparing] = useState(false);   // the Accounting Import File summary is loading (button label only)
  const [bulkVat, setBulkVat] = useState('');          // '' (nothing chosen) | 'default' | a tax type code

  const rootRef = useRef(null);
  const headingRef = useRef(null);
  const defaultsRef = useRef(null);
  const runBtnRef = useRef(null);
  const redoBtnRef = useRef(null);
  const toolbarHintId = useId();
  const [rebaseTick, setRebaseTick] = useState(0);     // bumped after a Redo action: "Reviewed X of Y" starts again from the new count
  const rebaseSeenRef = useRef(0);
  const cancelBtnRef = useRef(null);
  const cancelRef = useRef(false);
  const aiRunningRef = useRef(false);
  const prevRunningRef = useRef(false);
  const onAiRunningChangeRef = useRef(onAiRunningChange);
  const mountedRef = useRef(true);
  const dataRef = useRef(null);
  const txnMapRef = useRef(new Map());
  const accountsByIdRef = useRef(new Map());
  const renderedKeysRef = useRef([]);
  const navListRef = useRef([]);
  const learnRef = useRef(false);
  const filtersRef = useRef(DEFAULT_FILTERS);
  const unassignedFirstRef = useRef(false);
  const reviewModeRef = useRef(false);
  const inflightRef = useRef(0);
  const needsReloadRef = useRef(false);
  const loadSeqRef = useRef(0);
  const chainRef = useRef(new Map());
  const rowSyncRef = useRef(new Map());                // key -> { committed, count, failed } for in-flight single-row saves
  const vatSyncRef = useRef(new Map());                // key -> { committed, count, failed } for in-flight single-row VAT saves
  const initRef = useRef(false);
  const navCursorRef = useRef({ unassigned: -1, review: -1 });
  const scrollRef = useRef({ win: 0 });
  const scrollElsRef = useRef(new Map());              // scrolled ancestors of the root -> scrollTop (learned from scroll events, no layout reads)
  const wasActiveRef = useRef(active);
  const bulkRef = useRef(null);
  const bannerRef = useRef(null);
  const aiFocusRef = useRef(null);                     // 'run' | 'cancel' | null: which AI button had focus (so we only restore focus we took away)
  const rafIdsRef = useRef(new Set());                 // pending animation frames, cancelled on unmount
  const lastAnnounceRef = useRef('');
  const announceTimerRef = useRef(null);
  const touchRef = useRef(0);                          // last touch time: emulated mouseenter after a tap must not hold the notice
  const readOnly = !canEdit;

  learnRef.current = learnRows;
  filtersRef.current = filters;
  unassignedFirstRef.current = unassignedFirst;
  reviewModeRef.current = reviewMode;
  onAiRunningChangeRef.current = onAiRunningChange;

  useEffect(() => {
    mountedRef.current = true;
    const rafIds = rafIdsRef.current;
    return () => {
      mountedRef.current = false; cancelRef.current = true; // true unmount only: stop the AI loop
      for (const id of rafIds) cancelAnimationFrame(id);
      rafIds.clear();
      if (announceTimerRef.current) clearTimeout(announceTimerRef.current);
      if (onAiRunningChangeRef.current) onAiRunningChangeRef.current(false);
    };
  }, []);

  /** requestAnimationFrame that is cancelled when the component unmounts. */
  const raf = useCallback((fn) => {
    const id = requestAnimationFrame(() => { rafIdsRef.current.delete(id); if (mountedRef.current) fn(); });
    rafIdsRef.current.add(id);
    return id;
  }, []);

  // Identical consecutive messages would not be re-read by a screen reader: clear the region first, then set the text.
  const announce = useCallback((m) => {
    if (!mountedRef.current) return;
    if (announceTimerRef.current) { clearTimeout(announceTimerRef.current); announceTimerRef.current = null; }
    if (m && m === lastAnnounceRef.current) {
      setAnnouncement('');
      announceTimerRef.current = setTimeout(() => { announceTimerRef.current = null; if (mountedRef.current) setAnnouncement(m); }, 80);
    } else setAnnouncement(m);
    lastAnnounceRef.current = m;
  }, []);
  const showNotice = useCallback((text, action) => { if (mountedRef.current) { setNoticeHold(false); setNotice({ text, action }); } }, []);
  const askConfirm = useCallback((d) => setDlg(d), []);
  const onDefaultsError = useCallback((m) => setError(m), []);

  // Notices auto-dismiss after NOTICE_MS - but never while the tab is hidden, while the pointer/focus is on the
  // notice, or when the notice carries an action button (WCAG 2.2.1 timing). Errors stay until dismissed.
  useEffect(() => { if (!notice) setNoticeHold(false); }, [notice]);
  useEffect(() => {
    if (!notice || !active || noticeHold || notice.action) return undefined;
    const id = setTimeout(() => setNotice(null), NOTICE_MS);
    return () => clearTimeout(id);
  }, [notice, active, noticeHold]);

  // The parent can badge the tab while an AI run is in progress.
  useEffect(() => {
    if (ai.running === prevRunningRef.current) return;
    prevRunningRef.current = ai.running;
    if (onAiRunningChangeRef.current) onAiRunningChangeRef.current(ai.running);
    // The button that had focus (Run / Cancel) just unmounted: hand focus to its counterpart - but only if focus really
    // was on that button before (aiFocusRef) and is lost now; never pull focus away from where the user went meanwhile.
    const was = aiFocusRef.current;
    aiFocusRef.current = null;
    if (!focusLost()) return;
    if (ai.running) { if (was === 'run' && cancelBtnRef.current) cancelBtnRef.current.focus({ preventScroll: true }); }
    else if (was === 'cancel' && runBtnRef.current) runBtnRef.current.focus({ preventScroll: true });
  }, [ai.running]);

  // A hidden tab cannot show a dialog: close it so its document-level key trap never lingers.
  useEffect(() => { if (!active) setDlg(null); }, [active]);

  // ── Loading (stale-while-revalidate) ──
  const load = useCallback(async ({ silent = false } = {}) => {
    if (inflightRef.current > 0 && dataRef.current) { needsReloadRef.current = true; return; } // don't clobber optimistic rows
    const seq = ++loadSeqRef.current;
    if (!dataRef.current && !silent) setLoading(true); else setRefreshing(true);
    try {
      const res = await supplierReconAccountingAPI.get(sessionId);
      if (!mountedRef.current || seq !== loadSeqRef.current) return;
      if (inflightRef.current > 0) { needsReloadRef.current = true; return; }
      setData(res.data);
      setError(e => (e && e.startsWith('Failed to load') ? '' : e));
    } catch (err) {
      if (mountedRef.current && seq === loadSeqRef.current) setError(errMsg(err, 'Failed to load accounting data'));
    } finally {
      if (mountedRef.current && seq === loadSeqRef.current) { setLoading(false); setRefreshing(false); }
    }
  }, [sessionId]);

  useEffect(() => { load(); }, [load, groupId]);

  // Tab visibility: remember scroll while visible; on return, restore scroll and silently revalidate.
  // Scroll containers are learned from the scroll events themselves (an ancestor of the root that scrolled), so there is
  // no getComputedStyle per event; scrolls inside option lists are ignored.
  useEffect(() => {
    if (!active) return undefined;
    const onScroll = (e) => {
      const root = rootRef.current; const tgt = e.target;
      if (!root || !tgt) return;
      if (tgt === document || tgt === document.documentElement || tgt === document.body) { scrollRef.current.win = window.scrollY; return; }
      if (tgt.nodeType !== 1 || (tgt.getAttribute && tgt.getAttribute('role') === 'listbox')) return;
      if (tgt.contains(root)) scrollElsRef.current.set(tgt, tgt.scrollTop);
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => document.removeEventListener('scroll', onScroll, { capture: true });
  }, [active]);

  useEffect(() => {
    if (active && !wasActiveRef.current) {
      raf(() => {
        window.scrollTo(0, scrollRef.current.win);
        for (const [el, top] of scrollElsRef.current) if (el.isConnected) el.scrollTop = top;
      });
      if (dataRef.current) load({ silent: true });
    }
    wasActiveRef.current = active;
  }, [active, load, raf]);

  // ── Derived data ──
  dataRef.current = data;
  const accounts = useMemo(() => data?.accounts || [], [data]);
  const accountsById = useMemo(() => new Map(accounts.map(a => [a.id, a])), [accounts]);
  accountsByIdRef.current = accountsById;
  const accountIndex = useMemo(() => accounts.map(a => ({
    a,
    code: String(a.account_code || '').toLowerCase(),
    hay: `${a.account_code || ''} ${a.description || ''} ${a.account_type || ''}`.toLowerCase(),
  })), [accounts]);
  const accountTypes = useMemo(() => [...new Set(accounts.map(a => a.account_type).filter(Boolean))].sort(), [accounts]);
  const currency = data?.currency || 'ZAR';
  const vatEnabled = !!data?.taxTypesAvailable;
  const vatLabels = data?.taxTypes || VAT_FALLBACK;
  const supplierNames = useMemo(() => new Map((data?.suppliers || []).map(s => [s.supplierKey, s.supplierName])), [data]);

  const txnMap = useMemo(() => {
    const m = new Map();
    for (const s of (data?.suppliers || [])) for (const t of s.transactions) m.set(t.key, t);
    return m;
  }, [data]);
  txnMapRef.current = txnMap;

  const overall = useMemo(() => countOf((data?.suppliers || []).flatMap(s => s.transactions), accountsById), [data, accountsById]);
  // Facts for "Redo accounting" (dialog counts, disabled reasons) and the Reset button: computed from the loaded data only.
  const redoStats = useMemo(() => {
    let rowVat = 0; let supVat = 0; let removed = 0; let resettable = 0; let handSet = 0;
    for (const s of (data?.suppliers || [])) {
      if (s.hasTaxDefault) supVat++;
      for (const t of s.transactions) {
        if (t.removedAccount) removed++;
        if (!isAssigned(t, accountsById)) continue;
        if (t.taxTypeSource === 'row') rowVat++;
        if (t.assignment.status === 'suggested' && t.assignment.source !== 'manual') resettable++; // what "Reset suggestions" really removes
        if (t.assignment.status === 'suggested' && t.assignment.source === 'manual') handSet++;    // to review, set by hand (Accept all includes these)
      }
    }
    return { rowVat, supVat, vat: rowVat + supVat, removed, resettable, handSet };
  }, [data, accountsById]);
  const segConfirmed = overall.total > 0 ? (overall.confirmed / overall.total) * 100 : 0;
  const segSuggested = overall.total > 0 ? (overall.suggested / overall.total) * 100 : 0;
  const allDone = overall.total > 0 && overall.unassigned === 0 && overall.suggested === 0;
  const filtering = !!(filters.unassignedOnly || filters.needsReview || filters.accountType || filters.search.trim());

  const visibleSuppliers = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return (data?.suppliers || []).map(s => {
      if (q && !s.supplierName.toLowerCase().includes(q)) return null;
      let txns = s.transactions.filter(t => passesRowFilters(t, filters, accountsById));
      const rowFilter = filters.unassignedOnly || filters.needsReview || filters.accountType;
      if (rowFilter && txns.length === 0) return null;
      if (unassignedFirst) txns = txns.map((t, i) => [t, i]).sort((x, y) => rankOf(x[0], accountsById) - rankOf(y[0], accountsById) || x[1] - y[1]).map(x => x[0]);
      return { sup: s, txns, counts: countOf(s.transactions, accountsById) };
    }).filter(Boolean);
  }, [data, filters, unassignedFirst, accountsById]);

  // Flat, ordered views of what is on screen: `nav` = every visible row (rendered or not), `renderedKeys` = rows that
  // are actually in the DOM (supplier expanded and within the row cap).
  const { navList, renderedKeys } = useMemo(() => {
    const nav = []; const rendered = [];
    for (const v of visibleSuppliers) {
      const isOpen = expanded.has(v.sup.supplierKey);
      const limit = shown[v.sup.supplierKey] || ROW_PAGE;
      v.txns.forEach((t, idx) => {
        const assigned = isAssigned(t, accountsById);
        nav.push({ key: t.key, supKey: v.sup.supplierKey, idx, unassigned: !assigned, review: assigned && t.assignment.status === 'suggested' });
        if (isOpen && idx < limit) rendered.push(t.key);
      });
    }
    return { navList: nav, renderedKeys: rendered };
  }, [visibleSuppliers, accountsById, expanded, shown]);
  navListRef.current = navList;
  renderedKeysRef.current = renderedKeys;
  const renderedSet = useMemo(() => new Set(renderedKeys), [renderedKeys]);

  // First load: open the suppliers that still need work (collapsed when complete), within a row budget.
  useEffect(() => {
    if (!data || data.noGroup || initRef.current) return;
    initRef.current = true;
    const byId = new Map((data.accounts || []).map(a => [a.id, a]));
    const next = new Set(); let rows = 0;
    for (const s of data.suppliers) {
      const c = countOf(s.transactions, byId);
      if (c.unassigned === 0 && c.suggested === 0) continue;
      const n = Math.min(s.transactions.length, ROW_PAGE);
      if (next.size > 0 && rows + n > EXPAND_ROW_CAP) break;
      next.add(s.supplierKey); rows += n;
    }
    setExpanded(next);
  }, [data]);

  // Selection hygiene: forget selected keys that no longer exist (data reloaded, source switched, chart changed...).
  useEffect(() => {
    if (!data || data.noGroup) return;
    setSelected(prev => {
      if (prev.size === 0) return prev;
      const next = new Set(); let changed = false;
      for (const k of prev) { if (txnMap.has(k)) next.add(k); else changed = true; }
      return changed ? next : prev;
    });
  }, [txnMap, data]);

  // Review mode progress ("reviewed X of Y")
  useEffect(() => {
    if (!reviewMode) { setReviewBase(0); return; }
    if (rebaseTick !== rebaseSeenRef.current) { rebaseSeenRef.current = rebaseTick; setReviewBase(overall.suggested); return; }
    setReviewBase(b => Math.max(b, overall.suggested));
  }, [reviewMode, overall.suggested, rebaseTick]);

  /** Make a visible-but-unrendered row renderable: expand its supplier and extend the row cap to include it. */
  const revealEntry = useCallback((entry) => {
    setExpanded(prev => (prev.has(entry.supKey) ? prev : new Set(prev).add(entry.supKey)));
    setShown(prev => {
      const need = Math.ceil((entry.idx + 1) / ROW_PAGE) * ROW_PAGE;
      return (prev[entry.supKey] || ROW_PAGE) >= need ? prev : { ...prev, [entry.supKey]: need };
    });
  }, []);

  /**
   * Focus hand-over after a pick/accept/navigation. `keys` are candidates in priority order; the first one that is in
   * the DOM wins. A candidate that is visible but not rendered (collapsed supplier / beyond the 50-row cap) is revealed
   * first and the hand-over is retried after the re-render. If nothing works: the supplier toggle, then the heading.
   * `selectors` (instead of keys) hands focus to the first matching element inside the tab. With no control in the row
   * (read-only), the row itself (tabIndex -1) takes focus.
   */
  useEffect(() => {
    if (!pendingFocus) return;
    const pf = pendingFocus;
    const root = rootRef.current;
    let el = null;
    if (root) {
      if (pf.selectors) {
        for (const sel of pf.selectors) { el = root.querySelector(sel); if (el) break; }
      } else {
        for (const k of (pf.keys || [])) {
          if (!k) continue;
          const li = root.querySelector(`[data-row-key="${k}"]`);
          if (li) {
            el = (pf.target === 'accept' && li.querySelector('[data-accept-key]')) || li.querySelector('input[data-combobox-key]') || li;
            break;
          }
          const entry = navListRef.current.find(n => n.key === k);
          if (entry && !pf.revealed) { revealEntry(entry); setPendingFocus({ ...pf, revealed: true }); return; }
        }
        if (!el) {
          const firstKey = (pf.keys || []).find(Boolean);
          const t = firstKey ? txnMapRef.current.get(firstKey) : null;
          const supKey = pf.supKey || (t && t.supplierKey);
          if (supKey) el = [...root.querySelectorAll('[data-sup-toggle]')].find(x => x.getAttribute('data-sup-toggle') === supKey) || null;
        }
      }
    }
    if (!el) el = headingRef.current;
    if (el) {
      if (document.activeElement !== el) el.focus({ preventScroll: true });
      if (pf.scroll && el.scrollIntoView && !inViewport(el)) el.scrollIntoView({ block: pf.scroll });
    }
    setPendingFocus(null);
  }, [pendingFocus, revealEntry]);

  // ── Local state helpers ──
  const patchTxns = useCallback((updates) => setData(d => d && ({
    ...d,
    suppliers: d.suppliers.map(s => {
      if (!s.transactions.some(t => updates.has(t.key))) return s; // untouched suppliers keep identity
      return { ...s, transactions: s.transactions.map(t => {
        if (!updates.has(t.key)) return t;
        const assignment = updates.get(t.key);
        const nt = { ...t, assignment, removedAccount: null };
        // Un-assigning deletes the row on the server, and the VAT override lives on that row: fall back to the supplier default
        if (!assignment && t.taxTypeSource === 'row') { nt.taxType = s.defaultTaxType ?? 3; nt.taxTypeSource = s.defaultTaxType && s.defaultTaxType !== 3 ? 'supplier' : 'default'; }
        return nt;
      }) };
    }),
  })), []);

  /** Merge plain fields (VAT type) into transactions by key; untouched suppliers keep identity. */
  const patchFields = useCallback((updates) => setData(d => d && ({
    ...d,
    suppliers: d.suppliers.map(s => {
      if (!s.transactions.some(t => updates.has(t.key))) return s;
      return { ...s, transactions: s.transactions.map(t => (updates.has(t.key) ? { ...t, ...updates.get(t.key) } : t)) };
    }),
  })), []);

  const mkAssignment = (acct, extra = {}) => ({
    accountId: acct.id, accountCode: acct.account_code, accountDescription: acct.description,
    accountType: acct.account_type || '', status: 'confirmed', source: 'manual', confidence: null, reason: null, ...extra,
  });

  /** Neighbouring rows that are actually rendered (expanded supplier, within the row cap). */
  const neighboursOf = (key) => {
    const list = renderedKeysRef.current; const i = list.indexOf(key);
    return i >= 0 ? { next: list[i + 1] || null, prev: list[i - 1] || null } : { next: null, prev: null };
  };

  /** Will the row leave its place (dropped by a filter, or re-sorted by "unassigned first") once `nextAssignment` is applied? */
  const predictMove = (t, nextAssignment) => {
    const byId = accountsByIdRef.current; const nt = { ...t, assignment: nextAssignment };
    if (!passesRowFilters(nt, filtersRef.current, byId)) return true;
    return unassignedFirstRef.current && rankOf(nt, byId) !== rankOf(t, byId);
  };

  /**
   * Single-row save: optimistic, per-row pending, serialised per key. Never disables controls.
   * Consistency: `committed` tracks what the server is known to hold for the row (updated only on success). When the
   * last queued save of the row settles, the row shows `committed` if any save in the chain failed (so a failed A
   * followed by a successful B ends on B, and a successful A followed by a failed B ends on A), and a reload is scheduled.
   * `call` may resolve to { noop: true, message } when the server touched nothing (row vanished): treated like a failure
   * without an error banner.
   */
  const saveRow = useCallback((key, next, call, okMsg) => {
    let st = rowSyncRef.current.get(key);
    if (!st) {
      const cur = txnMapRef.current.get(key);
      st = { committed: cur ? cur.assignment : null, count: 0, failed: false };
      rowSyncRef.current.set(key, st);
    }
    st.count++;
    patchTxns(new Map([[key, next]]));
    setPendingKeys(p => { const n = new Set(p); n.add(key); return n; });
    setRowErrors(e => { if (!(key in e)) return e; const n = { ...e }; delete n[key]; return n; });
    inflightRef.current++;
    const run = (chainRef.current.get(key) || Promise.resolve())
      .then(async () => {
        try {
          const out = await call();
          if (out && out.noop) {
            st.failed = true; needsReloadRef.current = true;
            if (mountedRef.current) showNotice(out.message); // the notice is a status region: no second announcement
          } else {
            st.committed = next;
            if (okMsg) announce(okMsg);
          }
        } catch (err) {
          st.failed = true; needsReloadRef.current = true;
          if (!mountedRef.current) return;
          const detail = errMsg(err, '');
          const msg = isTimeout(err)
            ? "Couldn't save (timed out). Restored the previous account."
            : `Couldn't save. Restored the previous account.${detail ? ` ${detail}` : ''}`;
          setError(msg);
          setRowErrors(e => ({ ...e, [key]: msg }));
        }
      })
      .finally(() => {
        inflightRef.current--;
        st.count--;
        if (st.count === 0) {
          rowSyncRef.current.delete(key);
          if (chainRef.current.get(key) === run) chainRef.current.delete(key);
          if (mountedRef.current) {
            if (st.failed) patchTxns(new Map([[key, st.committed]]));
            setPendingKeys(p => { const n = new Set(p); n.delete(key); return n; });
          }
        }
        if (inflightRef.current === 0 && needsReloadRef.current && mountedRef.current) {
          needsReloadRef.current = false;
          load({ silent: true });
        }
      });
    chainRef.current.set(key, run);
    return run;
  }, [patchTxns, announce, showNotice, load]);

  const goToDefaults = useCallback(() => {
    setDefaultsOpen(true);
    setDefaultsTick(n => n + 1); // refresh the list even if the panel is already open
    // The panel header button takes focus (the 'View supplier defaults' button that was clicked is gone by then); the
    // section carries scroll-mt-32 so the sticky notices do not cover it.
    raf(() => {
      const panel = defaultsRef.current;
      if (!panel) return;
      const first = panel.querySelector('button');
      if (first) first.focus({ preventScroll: true });
      if (panel.scrollIntoView) panel.scrollIntoView({ block: 'start' });
    });
  }, [raf]);

  const onPick = useCallback((key, acct) => {
    const t = txnMapRef.current.get(key);
    if (!t) return;
    const learn = learnRef.current;
    const nextAssignment = mkAssignment(acct);
    const { next, prev } = neighboursOf(key);
    // When the edit moves/drops this row (unassigned-first sort, filters) the user's "next" row is the natural target
    const keys = predictMove(t, nextAssignment) ? [next, key, prev] : [key, next, prev];
    saveRow(key, nextAssignment, async () => {
      const r = await supplierReconAccountingAPI.assignWith(sessionId, [key], acct.id, learn, SAVE_CFG);
      if ((r.data?.updated ?? 1) === 0) {
        // The key no longer exists server-side (source switched / data changed in another tab): nothing was assigned
        return { noop: true, message: 'That transaction changed elsewhere (for example its source was switched), so no account was assigned. Refreshing.' };
      }
      if (learn) showNotice(`Remembered as default for ${t.supplierName}${t.kind === 'pl_line' && t.cat ? ` (${t.cat})` : ''}.`, { label: 'View supplier defaults', onClick: goToDefaults });
      return undefined;
    }, `Account set to ${acct.account_code} for ${txnName(t)}`);
    setPendingFocus({ keys, target: 'combobox', scroll: 'nearest' });
  }, [saveRow, sessionId, showNotice, goToDefaults]); // eslint-disable-line react-hooks/exhaustive-deps

  const onClear = useCallback((key) => {
    const t = txnMapRef.current.get(key);
    if (!t) return;
    const { next, prev } = neighboursOf(key);
    const keys = predictMove(t, null) ? [next, key, prev] : [key, next, prev];
    saveRow(key, null, () => supplierReconAccountingAPI.unassignWith(sessionId, [key], SAVE_CFG), `Account cleared for ${txnName(t)}`);
    setPendingFocus({ keys, target: 'combobox', scroll: 'nearest' });
  }, [saveRow, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onAccept = useCallback((key) => {
    const t = txnMapRef.current.get(key);
    if (!t || !t.assignment) return;
    const nextAssignment = { ...t.assignment, status: 'confirmed' };
    const { next, prev } = neighboursOf(key);
    let keys = predictMove(t, nextAssignment) ? [next, key, prev] : [key, next, prev];
    let target = 'combobox'; let scroll = 'nearest';
    if (reviewModeRef.current) {
      // Review mode: accept, then jump to the next row that still needs review. That row may be collapsed or beyond the
      // row cap; the focus hand-over reveals it first (same as "Go to next").
      const nav = navListRef.current; const i = nav.findIndex(n => n.key === key);
      const nxt = nav.slice(i + 1).find(n => n.review) || nav.find(n => n.review && n.key !== key);
      scroll = 'center';
      if (nxt) { keys = [nxt.key, next, prev, key]; target = 'accept'; }
    }
    saveRow(key, nextAssignment, async () => {
      const r = await supplierReconAccountingAPI.confirmWith(sessionId, { txnKeys: [key] }, SAVE_CFG);
      if ((r.data?.confirmed ?? 0) === 0) return { noop: true, message: 'That suggestion was already changed elsewhere. Refreshing.' };
      return undefined;
    }, `Suggestion accepted for ${txnName(t)}`);
    setPendingFocus({ keys, target, scroll });
  }, [saveRow, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── VAT types ──
  const supplierVatOf = (supplierKey) => {
    const s = (dataRef.current?.suppliers || []).find(x => x.supplierKey === supplierKey);
    return s && s.defaultTaxType ? s.defaultTaxType : 3;
  };
  /** Effective VAT fields of a row once `value` (a tax type, or null = follow the supplier default) is applied. */
  const vatFields = (value, supDefault) => (value === null
    ? { taxType: supDefault, taxTypeSource: supDefault === 3 ? 'default' : 'supplier' }
    : { taxType: value, taxTypeSource: 'row' });
  const vatLabelOf = (v) => vatLabelFor(vatLabels, v);

  /**
   * Single-row VAT save: same machine as saveRow (optimistic, serialised per row, `committed` = what the server is known
   * to hold, rollback to it + a reload when any save of the row failed), but for the VAT fields.
   */
  const onSetVat = useCallback((key, raw) => {
    const t = txnMapRef.current.get(key);
    if (!t) return;
    const value = raw === '' ? null : Number(raw);
    const next = vatFields(value, supplierVatOf(t.supplierKey));
    let st = vatSyncRef.current.get(key);
    if (!st) { st = { committed: { taxType: t.taxType, taxTypeSource: t.taxTypeSource }, count: 0, failed: false }; vatSyncRef.current.set(key, st); }
    st.count++;
    patchFields(new Map([[key, next]]));
    setPendingKeys(p => { const n = new Set(p); n.add(key); return n; });
    setRowErrors(e => { if (!(key in e)) return e; const n = { ...e }; delete n[key]; return n; });
    inflightRef.current++;
    const ck = `vat:${key}`;
    const run = (chainRef.current.get(ck) || Promise.resolve())
      .then(async () => {
        try {
          const r = await supplierReconAccountingAPI.setTaxType(sessionId, [key], value, SAVE_CFG);
          if ((r.data?.updated ?? 1) === 0) {
            st.failed = true; needsReloadRef.current = true;
            if (mountedRef.current) showNotice('That transaction no longer has an account, so its VAT type was not saved. Refreshing.');
          } else {
            st.committed = next;
            announce(value === null ? `VAT type for ${txnName(t)} follows the supplier: ${vatLabelOf(next.taxType)}` : `VAT type for ${txnName(t)} set to ${vatLabelOf(value)}`);
          }
        } catch (err) {
          st.failed = true; needsReloadRef.current = true;
          if (!mountedRef.current) return;
          const detail = errMsg(err, '');
          const msg = isTimeout(err)
            ? "Couldn't save the VAT type (timed out). Restored the previous VAT type."
            : `Couldn't save the VAT type. Restored the previous VAT type.${detail ? ` ${detail}` : ''}`;
          setError(msg);
          setRowErrors(e => ({ ...e, [key]: msg }));
        }
      })
      .finally(() => {
        inflightRef.current--;
        st.count--;
        if (st.count === 0) {
          vatSyncRef.current.delete(key);
          if (chainRef.current.get(ck) === run) chainRef.current.delete(ck);
          if (mountedRef.current) {
            if (st.failed) patchFields(new Map([[key, st.committed]]));
            if (!rowSyncRef.current.has(key)) setPendingKeys(p => { const n = new Set(p); n.delete(key); return n; });
          }
        }
        if (inflightRef.current === 0 && needsReloadRef.current && mountedRef.current) {
          needsReloadRef.current = false;
          load({ silent: true });
        }
      });
    chainRef.current.set(ck, run);
  }, [patchFields, announce, showNotice, load, sessionId, vatLabels]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSelected = useCallback((key) => setSelected(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; }), []);
  const toggleSelectMany = (keys, on) => setSelected(prev => { const n = new Set(prev); keys.forEach(k => (on ? n.add(k) : n.delete(k))); return n; });
  const toggleExpanded = (key) => setExpanded(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const dismissRowError = useCallback((key) => {
    setRowErrors(e => { const n = { ...e }; delete n[key]; return n; });
    setPendingFocus({ keys: [key], target: 'combobox' });
  }, []);
  const dismissError = () => { setError(''); setPendingFocus({ selectors: [] }); }; // the dismiss button unmounts: focus the heading
  const dismissNotice = () => { setNotice(null); setPendingFocus({ selectors: [] }); };

  // ── Bulk / global actions (these may disable controls via `busy`) ──
  // `fn` may return { reload: true, notice } to force a reload and replace the success text (partial results).
  const act = async (fn, { refetch = true, success = '' } = {}) => {
    setBusy(true); setError('');
    let ok = false;
    try {
      const out = await fn();
      ok = true;
      if (out && out.notice) showNotice(out.notice); else if (success) showNotice(success);
      if ((refetch || (out && out.reload)) && mountedRef.current) await load({ silent: true });
    } catch (err) {
      if (mountedRef.current) {
        setError(isTimeout(err)
          ? "Couldn't save (timed out). Reloaded the current accounts; check them before trying again."
          : await errMsgAsync(err, 'That action failed. Please try again.'));
        await load({ silent: true });
      }
    } finally {
      if (mountedRef.current) setBusy(false);
    }
    return ok;
  };

  /** Counts come from the server (rows really written / removed), never from what was requested. */
  const assignKeys = (keys, acct, learn) => act(async () => {
    patchTxns(new Map(keys.map(k => [k, mkAssignment(acct)])));
    let updated = 0;
    for (const part of chunkKeys(keys)) {
      const r = await supplierReconAccountingAPI.assignWith(sessionId, part, acct.id, !!learn, SAVE_CFG);
      updated += Number(r?.data?.updated ?? part.length);
    }
    if (updated < keys.length) return { reload: true, notice: `${updated} of ${keys.length} updated; the rest changed elsewhere. Reloaded.` };
    return { notice: `${plural(updated, 'transaction')} assigned to ${acct.account_code}.` };
  }, { refetch: false });

  const clearKeys = (keys) => act(async () => {
    patchTxns(new Map(keys.map(k => [k, null])));
    let cleared = 0;
    for (const part of chunkKeys(keys)) {
      const r = await supplierReconAccountingAPI.unassignWith(sessionId, part, SAVE_CFG);
      cleared += Number(r?.data?.cleared ?? part.length);
    }
    if (cleared < keys.length) return { reload: true, notice: `${cleared} of ${keys.length} cleared; the rest changed elsewhere. Reloaded.` };
    return { notice: `${plural(cleared, 'account')} cleared.` };
  }, { refetch: false });

  /** The bulk bar unmounts when the selection empties: hand focus to a row checkbox (or the heading) if focus was in it or lost. */
  const focusAfterBulk = (firstKey) => {
    const ae = document.activeElement; const bar = bulkRef.current;
    if (!focusLost() && !(bar && ae && bar.contains(ae)) && ae !== headingRef.current) return; // heading = the dialog fallback when the opener was disabled
    setPendingFocus({ selectors: firstKey ? [`[data-row-key="${firstKey}"] input[type="checkbox"]`] : [] });
  };
  const clearSelection = () => { focusAfterBulk([...selected][0]); setSelected(new Set()); };

  const acceptAll = () => act(async () => {
    const r = await supplierReconAccountingAPI.confirm(sessionId, { all: true });
    showNotice(`${plural(r.data?.confirmed ?? 0, 'suggestion')} accepted.`);
  });

  const resetSuggestions = () => askConfirm({
    title: `Reset ${plural(redoStats.resettable, 'suggestion')}?`,
    body: (
      <>
        <p>{plural(redoStats.resettable, 'AI or default suggestion')} that {redoStats.resettable === 1 ? 'has' : 'have'} not been accepted will be cleared.</p>
        <p>Manually assigned and confirmed accounts are kept.</p>
      </>
    ),
    confirmLabel: `Reset ${plural(redoStats.resettable, 'suggestion')}`,
    danger: true,
    onConfirm: () => act(async () => {
      const r = await supplierReconAccountingAPI.reset(sessionId);
      showNotice(`${plural(r.data?.cleared ?? 0, 'suggestion')} cleared.`);
    }),
  });

  // ── Redo accounting (accounting only: the recon, its payments and files are never touched) ──
  const redoBusyReason = ai.running ? 'AI matching is running' : busy ? 'Wait for the current action to finish' : null;

  /** Runs one Redo call through act() (busy state, error banner incl. the server's 409 text, reload) and parks focus on the Redo button. */
  const runRedo = async (call, noticeOf) => {
    await act(async () => {
      const r = await call();
      setRowErrors({});
      return { notice: noticeOf((r && r.data) || {}) };
    });
    if (!mountedRef.current) return;
    setRebaseTick(t => t + 1);
    if (focusLost() || document.activeElement === headingRef.current) setPendingFocus({ selectors: ['[data-redo-btn]'] });
  };

  const askUnconfirmAll = () => {
    if (inflightRef.current > 0) { showNotice('Please wait for your changes to finish saving, then try again.'); return; }
    const n = overall.confirmed;
    askConfirm({
      title: 'Unconfirm all confirmed transactions?',
      body: (
        <>
          <p className="font-semibold">Your recon, payments and files are not changed.</p>
          <p>
            {plural(n, 'transaction')} {n === 1 ? 'goes' : 'go'} back to “To review”.{' '}
            {n === 1 ? 'It keeps its account and VAT type, so you can check it and confirm it again.' : 'They keep their accounts and VAT types, so you can check them and confirm them again.'}
          </p>
        </>
      ),
      confirmLabel: 'Unconfirm all',
      amber: true,
      onConfirm: () => runRedo(
        () => supplierReconAccountingAPI.unconfirmAll(sessionId),
        (d) => { const u = Number(d.unconfirmed ?? 0); return u === 0 ? 'Nothing to unconfirm.' : `${plural(u, 'transaction')} unconfirmed.`; },
      ),
    });
  };

  const askClearAll = () => {
    if (inflightRef.current > 0) { showNotice('Please wait for your changes to finish saving, then try again.'); return; }
    // V mirrors what the server resets: row-level VAT types vanish with the deleted rows (rowVat) and the session's supplier
    // VAT defaults are set to NULL (supVat = suppliers with hasTaxDefault).
    const n = overall.assigned; const c = overall.confirmed; const sg = overall.suggested; const v = redoStats.vat;
    const extra = (data?.orphaned?.count || 0) + redoStats.removed;
    askConfirm({
      title: 'Clear all accounting for this recon?',
      body: (
        <>
          <p className="font-semibold">Your recon, payments and files are not changed.</p>
          <ul className="list-disc pl-5 space-y-1" data-clear-summary="">
            <li>{plural(n, 'account')} removed ({c} confirmed, {sg} still to review)</li>
            <li>{v} VAT {v === 1 ? 'choice' : 'choices'} reset (row-level VAT types and this recon&apos;s supplier VAT defaults)</li>
            <li>Kept: the recon, its payments and files, and the supplier defaults we remember for future recons (the account and VAT type per supplier).</li>
          </ul>
          {extra > 0 && <p>{plural(extra, 'assignment')} that no longer {extra === 1 ? 'matches' : 'match'} a current transaction or account {extra === 1 ? 'is' : 'are'} removed too.</p>}
          <p className="font-semibold text-red-800">This cannot be undone.</p>
        </>
      ),
      confirmLabel: 'Clear everything',
      danger: true,
      onConfirm: () => runRedo(
        () => supplierReconAccountingAPI.clearAll(sessionId),
        (d) => {
          const del = Number(d.deleted ?? 0); const vr = Number(d.vatReset ?? 0);
          if (del === 0 && vr === 0) return 'Nothing to clear.';
          if (del === 0) return `Accounting cleared. ${plural(vr, 'VAT choice')} reset.`;
          return `Accounting cleared. ${plural(del, 'account')} removed.`;
        },
      ),
    });
  };

  const unassignSelected = () => {
    // Only rows that really have an account are cleared and counted; the rest of the selection is ignored.
    const keys = []; let confirmedN = 0; let suggestedN = 0;
    for (const k of selected) {
      const t = txnMap.get(k);
      if (t && isAssigned(t, accountsById)) { keys.push(k); if (t.assignment.status === 'confirmed') confirmedN++; else suggestedN++; }
    }
    const hidden = keys.filter(k => !renderedSet.has(k)).length;
    const ignored = selected.size - keys.length;
    if (keys.length === 0) { showNotice('None of the selected transactions has an account to remove.'); return; }
    const first = keys[0];
    const run = async () => { if (await clearKeys(keys)) { if (mountedRef.current) { focusAfterBulk(first); setSelected(new Set()); } } };
    if (keys.length > UNASSIGN_CONFIRM_OVER || hidden > 0) {
      askConfirm({
        title: `Unassign ${plural(keys.length, 'transaction')}?`,
        body: (
          <>
            <p>The accounts on {plural(keys.length, 'selected transaction')} will be removed ({confirmedN} confirmed, {suggestedN} to review). You can pick accounts again afterwards.</p>
            {hidden > 0 && <p>{hidden} of them {hidden === 1 ? 'is' : 'are'} not currently shown (hidden by a filter, a collapsed supplier or the row limit).</p>}
            {ignored > 0 && <p>{plural(ignored, 'selected transaction')} without an account {ignored === 1 ? 'is' : 'are'} ignored.</p>}
          </>
        ),
        confirmLabel: `Unassign ${keys.length}`, danger: true, onConfirm: run,
      });
    } else run();
  };

  const assignSelected = async () => {
    if (!bulkAccount) return;
    const keys = [...selected]; const acct = bulkAccount; const learn = bulkLearn;
    const run = async () => {
      const ok = await assignKeys(keys, acct, learn); // selection is kept when the save fails
      if (ok && mountedRef.current) { focusAfterBulk(keys[0]); setSelected(new Set()); }
    };
    const hidden = keys.filter(k => !renderedSet.has(k)).length;
    if (hidden > 0) {
      // Same safeguard as Unassign: rows the user cannot see are part of the selection
      let overwrite = 0;
      for (const k of keys) { const t = txnMap.get(k); if (t && isAssigned(t, accountsById)) overwrite++; }
      askConfirm({
        title: `Assign ${plural(keys.length, 'transaction')}?`,
        body: (
          <>
            <p>{acct.account_code} will be set on {plural(keys.length, 'selected transaction')}. {hidden} of them {hidden === 1 ? 'is' : 'are'} not currently shown (hidden by a filter, a collapsed supplier or the row limit).</p>
            {overwrite > 0 && <p>{plural(overwrite, 'transaction')} already {overwrite === 1 ? 'has' : 'have'} an account that will be replaced.</p>}
          </>
        ),
        confirmLabel: `Assign ${keys.length}`, onConfirm: run,
      });
    } else run();
  };

  const applyToSupplier = (sup) => {
    const st = applyState[sup.supplierKey] || {};
    if (!st.account) return;
    const scope = st.scope || 'unassigned';
    const run = () => act(async () => {
      const r = await supplierReconAccountingAPI.applySupplier(sessionId, {
        supplierName: sup.supplierName, accountId: st.account.id, scope, learn: !!st.learn,
      });
      showNotice(`${plural(r.data?.updated ?? 0, 'transaction')} updated for ${sup.supplierName}.${st.learn ? ` Remembered as default for ${sup.supplierName}.` : ''}`,
        st.learn ? { label: 'View supplier defaults', onClick: goToDefaults } : undefined);
    });
    const c = countOf(sup.transactions, accountsById);
    if (scope === 'all' && c.assigned > 0) {
      askConfirm({
        title: 'Replace existing accounts?',
        body: (
          <>
            <p>This will overwrite {plural(c.assigned, 'existing account')} for {sup.supplierName} ({c.confirmed} confirmed, {c.suggested} to review) with {st.account.account_code}.</p>
            <p>{c.unassigned} unassigned {c.unassigned === 1 ? 'transaction' : 'transactions'} will also be assigned.</p>
          </>
        ),
        confirmLabel: `Replace ${c.assigned} and apply`, danger: true, onConfirm: run,
      });
    } else run();
  };

  const setSource = (sup, source) => act(async () => {
    await supplierReconAccountingAPI.setSupplierSource(sessionId, sup.supplierName, source);
  });

  /** Hands focus back to the supplier's VAT select (else the heading) when an action left it on <body>. */
  const restoreVatFocus = (supplierKey) => {
    // heading = where the confirm dialog falls back to when the button that opened it disappeared (the reset button unmounts)
    if (mountedRef.current && (focusLost() || document.activeElement === headingRef.current)) setPendingFocus({ selectors: [`[data-vat-supplier="${attrVal(supplierKey)}"] select`] });
  };

  /**
   * Supplier default VAT type. Rows with their own VAT type keep it (use "Reset N row overrides" to clear them).
   * The select is never disabled while this runs (that would drop keyboard focus to <body>); extra changes are ignored.
   * Failure reloads = rollback to what the server holds.
   */
  const setSupplierVat = async (sup, raw) => {
    if (busy || ai.running) return;
    const taxType = Number(raw);
    if (taxType === sup.defaultTaxType) return;
    await act(async () => {
      setData(d => d && ({
        ...d,
        suppliers: d.suppliers.map(s => (s.supplierKey !== sup.supplierKey ? s : {
          ...s,
          defaultTaxType: taxType,
          transactions: s.transactions.map(t => (t.taxTypeSource !== 'row' ? { ...t, taxType, taxTypeSource: 'supplier' } : t)),
        })),
      }));
      await supplierReconAccountingAPI.setSupplierTaxType(sessionId, sup.supplierName, taxType, false, SAVE_CFG);
      return { notice: `VAT type for ${sup.supplierName} set to ${vatLabelOf(taxType)}.` };
    }, { refetch: false });
    restoreVatFocus(sup.supplierKey);
  };

  /** Clears every row-level VAT type of the supplier so all rows follow the supplier's current VAT type. */
  const resetSupplierOverrides = async (sup) => {
    if (busy || ai.running) return;
    const taxType = sup.defaultTaxType || 3;
    await act(async () => {
      setData(d => d && ({
        ...d,
        suppliers: d.suppliers.map(s => (s.supplierKey !== sup.supplierKey ? s : {
          ...s,
          transactions: s.transactions.map(t => (t.taxTypeSource === 'row' ? { ...t, taxType, taxTypeSource: 'supplier' } : t)),
        })),
      }));
      const r = await supplierReconAccountingAPI.setSupplierTaxType(sessionId, sup.supplierName, taxType, true, SAVE_CFG);
      const cleared = Number(r?.data?.clearedOverrides ?? 0);
      return { notice: `${plural(cleared, 'row override')} reset. All ${sup.supplierName} rows now use ${vatLabelOf(taxType)}.` };
    }, { refetch: false });
    restoreVatFocus(sup.supplierKey);
  };

  const askResetOverrides = (sup, n) => askConfirm({
    title: `Reset ${plural(n, 'row override')} for ${sup.supplierName}?`,
    body: (
      <p>{plural(n, 'transaction')} of {sup.supplierName} {n === 1 ? 'has its own VAT type' : 'have their own VAT types'}. {n === 1 ? 'It' : 'They'} will all use {vatLabelOf(sup.defaultTaxType || 3)} instead.</p>
    ),
    confirmLabel: `Reset ${plural(n, 'row override')}`,
    onConfirm: () => resetSupplierOverrides(sup),
  });

  /** Bulk VAT type: only rows that have an account can hold an override; the rest of the selection is reported, not changed. */
  const setVatSelected = async () => {
    if (!bulkVat || busy || ai.running) return;
    const value = bulkVat === 'default' ? null : Number(bulkVat);
    const keys = [...selected];
    const assigned = keys.filter(k => { const t = txnMap.get(k); return t && isAssigned(t, accountsById); });
    const skipped = keys.length - assigned.length;
    if (assigned.length === 0) {
      showNotice('None of the selected transactions has an account yet, so a row VAT type cannot be set. Assign accounts first, or set the VAT type on the supplier.');
      return;
    }
    const ok = await act(async () => {
      patchFields(new Map(assigned.map(k => [k, vatFields(value, supplierVatOf(txnMap.get(k).supplierKey))])));
      let updated = 0;
      for (const part of chunkKeys(assigned)) {
        const r = await supplierReconAccountingAPI.setTaxType(sessionId, part, value, SAVE_CFG);
        updated += Number(r?.data?.updated ?? part.length);
      }
      if (updated < assigned.length) return { reload: true, notice: `${updated} of ${assigned.length} updated; the rest changed elsewhere. Reloaded.` };
      const what = value === null ? 'now follow the supplier VAT type' : `set to ${vatLabelOf(value)}`;
      return { notice: `${plural(updated, 'transaction')} ${what}.${skipped > 0 ? ` ${plural(skipped, 'selected transaction')} without an account ${skipped === 1 ? 'was' : 'were'} skipped.` : ''}` };
    }, { refetch: false });
    if (ok && mountedRef.current) { focusAfterBulk(keys[0]); setSelected(new Set()); setBulkVat(''); }
    else if (mountedRef.current && focusLost()) setPendingFocus({ selectors: ['[data-bulk-vat-select]', '[data-bulk-vat-apply]'] });
  };

  const relinkOrphan = async (item) => {
    const items = (dataRef.current?.orphaned?.items) || [];
    const i = items.findIndex(o => o.key === item.key);
    const neighbours = [items[i + 1], items[i - 1]].filter(Boolean).map(o => o.key);
    await act(async () => {
      const r = await supplierReconAccountingAPI.relink(sessionId, [item.key]);
      showNotice((r.data?.relinked ?? 0) > 0 ? `Re-linked the ${item.txn_ref || 'assignment'} to its current transaction.` : 'Could not re-link: the transaction changed. Review it manually.');
    });
    // The Re-link button is gone now: hand focus to the next item in the banner, else the list toggle, else the heading.
    // Only when focus is still lost or inside the banner - never steal it from somewhere the user moved to meanwhile.
    const ae = document.activeElement; const banner = bannerRef.current;
    if (mountedRef.current && (focusLost() || (banner && ae && banner.contains(ae)))) {
      setPendingFocus({ selectors: [
        ...neighbours.flatMap(k => [`[data-orphan-relink="${k}"]`, `[data-orphan-go="${k}"]`]),
        '[data-orphan-toggle]', '[data-orphan-again]',
      ] });
    }
  };

  /** Bring the supplier (and the transaction with the same reference, if there is one) into view. */
  const jumpToOrphan = (o) => {
    const wanted = String(o.supplier_name || '').trim().toLowerCase();
    const sup = (data?.suppliers || []).find(s => s.supplierKey === wanted || String(s.supplierName).trim().toLowerCase() === wanted);
    if (!sup) { showNotice(`${o.supplier_name || 'That supplier'} is not listed on this tab (it may not be accepted, or the other source is counted).`); return; }
    const ref = String(o.txn_ref || '').trim().toLowerCase();
    const cand = ref ? sup.transactions.find(t => String(t.ref || '').trim().toLowerCase() === ref || String(t.purchaseRef || '').trim().toLowerCase() === ref) : null;
    if (filtering) setFilters(DEFAULT_FILTERS);
    setExpanded(prev => new Set(prev).add(sup.supplierKey));
    if (!cand) showNotice(`No current transaction with reference ${o.txn_ref || '(none)'} under ${sup.supplierName}. Showing the supplier instead.`);
    setPendingFocus({ keys: cand ? [cand.key] : [], supKey: sup.supplierKey, target: 'combobox', scroll: 'center' });
  };

  const dismissOrphans = (sig) => { setOrphanDismissed(sig); setPendingFocus({ selectors: ['[data-orphan-again]'] }); };

  // ── AI loop ──
  const sleep = (ms) => new Promise(r => setTimeout(r, ms));

  /**
   * One server request = at most ONE AI call. Retry at most once, only for 429 (rejected before any work) or
   * network/5xx errors; a cursor that already returned successfully is never re-sent (the caller moves on).
   * A retry after a timeout can re-send transactions the AI had already been asked about (small extra cost);
   * assigned ones are never re-sent because the server skips them.
   */
  const callAutoAssign = async (cursor) => {
    try {
      return await supplierReconAccountingAPI.autoAssign(sessionId, cursor);
    } catch (err) {
      const status = err?.response?.status;
      if (cancelRef.current || !(!status || status === 429 || status >= 500)) throw err;
      const wait = status === 429 ? Math.min(Number(err.response.data?.retryAfter) || 4, 8) * 1000 : 3000;
      await sleep(wait);
      if (cancelRef.current) throw err;
      return supplierReconAccountingAPI.autoAssign(sessionId, cursor);
    }
  };

  const runAi = async () => {
    if (aiRunningRef.current) return;
    aiRunningRef.current = true;
    cancelRef.current = false;
    if (document.activeElement && document.activeElement === runBtnRef.current) aiFocusRef.current = 'run';
    setError(''); setNotice(null);
    setAi({ running: true, cancelling: false, done: 0, total: 0 });
    announce('AI matching started');
    let cursor = null; let failed = 0; let unmatched = 0; let skipped = false; let cancelled = false; let capped = false; let stuck = false;
    let milestone = 0;
    try {
      for (let guard = 0; guard < 1000; guard++) {
        if (cancelRef.current) { cancelled = true; break; }
        const r = (await callAutoAssign(cursor)).data;
        failed += r.failedWindows || 0;
        unmatched += r.unmatched || 0;
        skipped = skipped || !!r.aiSkipped;
        capped = capped || !!r.capped;
        if (mountedRef.current) setAi(a => ({ ...a, done: r.suppliersDone ?? a.done, total: r.suppliersTotal ?? a.total }));
        const pctDone = r.suppliersTotal ? Math.floor(((r.suppliersDone || 0) / r.suppliersTotal) * 4) * 25 : 0;
        if (pctDone > milestone && pctDone < 100) { milestone = pctDone; announce(`AI matching ${pctDone} percent done`); }
        if (!r.nextCursor || r.remaining === 0) break;
        if (r.nextCursor === cursor) { stuck = true; break; }
        cursor = r.nextCursor;
      }
      const parts = [];
      if (cancelled) parts.push('AI matching cancelled. Suggestions made so far were kept.');
      if (skipped) parts.push('AI is not configured, so only remembered defaults were applied.');
      if (capped) parts.push('AI matching reached its limit for one run. Run it again to continue.');
      if (failed > 0 || stuck) parts.push('Some suppliers could not be matched. Run AI matching again to retry.');
      if (unmatched > 0 && !cancelled) parts.push(`${plural(unmatched, 'transaction')} had no suitable account suggestion.`);
      if (!cancelled && !skipped && !capped && failed === 0 && !stuck) parts.push('AI matching finished. Review the suggestions and accept them.');
      if (mountedRef.current) {
        const text = parts.join(' ');
        // The notice (status) / error (alert) region announces the result itself: no second announcement for the same event
        if (failed > 0 || stuck) setError(text); else showNotice(text);
      }
    } catch (err) {
      const msg = await errMsgAsync(err, 'AI matching stopped because of an error. Suggestions made so far were kept.');
      if (mountedRef.current) setError(msg);
    } finally {
      aiRunningRef.current = false;
      // After a true unmount: no state updates and no extra GET
      if (mountedRef.current) {
        setAi({ running: false, cancelling: false, done: 0, total: 0 });
        await load({ silent: true });
      }
    }
  };

  const cancelAi = () => {
    if (document.activeElement && document.activeElement === cancelBtnRef.current) aiFocusRef.current = 'cancel';
    cancelRef.current = true; setAi(a => ({ ...a, cancelling: true }));
  };

  // ── Export ──
  const exportCsv = async () => {
    setBusy(true); setError('');
    announce('Export started'); // announced once; the result notice below does not repeat it
    try {
      const res = await supplierReconAccountingAPI.exportCsv(sessionId);
      const cd = res.headers?.['content-disposition'] || '';
      const m = cd.match(/filename="?([^";]+)"?/i);
      const filename = m ? m[1] : `accounting-${new Date().toISOString().slice(0, 10)}.csv`;
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url; link.download = filename;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      showNotice(`CSV file sent to your downloads. ${overall.unassigned > 0 ? `${plural(overall.unassigned, 'transaction')} ${overall.unassigned === 1 ? 'is' : 'are'} still unassigned and exported as "Unassigned".` : 'All transactions are linked.'}`);
    } catch (err) {
      const msg = await errMsgAsync(err, 'Failed to export CSV');
      if (mountedRef.current) setError(msg);
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  /** Accounting Import File (XLSX): downloads the file for the confirmed transactions. `sum` is the export summary. */
  const downloadImportFile = async (sum) => {
    setBusy(true); setError('');
    announce('Export started'); // announced once; the result notice below does not repeat it
    try {
      const res = await supplierReconAccountingAPI.downloadImportFile(sessionId);
      const filename = filenameFrom(res.headers?.['content-disposition'], sum.filename || `Accounting Import File - ${new Date().toISOString().slice(0, 10)}.xlsx`);
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url; link.download = filename;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      const headerRows = Number.parseInt(res.headers?.['x-row-count'], 10);
      const rows = Number.isFinite(headerRows) && headerRows >= 0 ? headerRows : sum.rowsToExport;
      showNotice(`Accounting Import File downloaded (${plural(rows, 'row')}).${sum.taxTypesAvailable === false ? ` ${VAT_UNAVAILABLE}, so every row uses ${vatLabelFor(vatLabels, 3)}.` : ''}`);
    } catch (err) {
      const msg = await errMsgAsync(err, 'Failed to export the Accounting Import File');
      if (mountedRef.current) setError(msg);
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  /**
   * Asks the server what the file would contain first; a dialog is shown only when there is something to warn about.
   * The button stays enabled (aria-disabled while busy) so keyboard focus is never dropped onto <body>.
   */
  const exportImportFile = async () => {
    if (busy || ai.running) return;
    setBusy(true); setPreparing(true); setError('');
    announce('Preparing the Accounting Import File');
    let sum = null;
    try {
      sum = (await supplierReconAccountingAPI.getExportSummary(sessionId)).data;
    } catch (err) {
      const msg = await errMsgAsync(err, 'Failed to prepare the Accounting Import File');
      if (mountedRef.current) setError(msg);
    } finally {
      if (mountedRef.current) { setBusy(false); setPreparing(false); }
    }
    if (!sum || !mountedRef.current) return;
    const notConfirmed = (sum.leftOutUnconfirmed || 0) + (sum.leftOutNoAccount || 0);
    const suggestions = sum.leftOutUnconfirmed || 0;
    const missingCodes = Array.isArray(sum.suppliersMissingCode) ? sum.suppliersMissingCode : [];
    const missingDate = sum.missingDate || 0;
    if (!sum.rowsToExport) {
      setError(`Nothing to export yet. Only confirmed transactions go into the Accounting Import File, and none are confirmed${suggestions > 0 ? ` (${suggestions} ${suggestions === 1 ? 'is' : 'are'} still ${suggestions === 1 ? 'a suggestion' : 'suggestions'})` : ''}. Accept suggestions or assign accounts, then try again.`);
      return;
    }
    if (notConfirmed === 0 && missingCodes.length === 0 && missingDate === 0) { downloadImportFile(sum); return; }
    const shownNames = missingCodes.slice(0, MAX_NAMES_SHOWN).join(', ');
    const moreNames = missingCodes.length - MAX_NAMES_SHOWN;
    askConfirm({
      title: 'Download the Accounting Import File?',
      body: (
        <>
          <p>{plural(sum.rowsToExport, 'transaction')} will be included.</p>
          {notConfirmed > 0 && (
            <p>{plural(notConfirmed, 'transaction')} {notConfirmed === 1 ? 'is' : 'are'} not confirmed yet, so {notConfirmed === 1 ? 'it' : 'they'} will NOT be in the file. Accept or assign {notConfirmed === 1 ? 'it' : 'them'} first if you need {notConfirmed === 1 ? 'it' : 'them'}.</p>
          )}
          {missingCodes.length > 0 && (
            <p>These suppliers have no creditor code, so the Account Code column will be empty for them: {shownNames}{moreNames > 0 ? ` and ${moreNames} more` : ''}. Add the supplier code on the Suppliers page first (Suppliers in the main menu), or fill it in after importing.</p>
          )}
          {missingDate > 0 && <p>{plural(missingDate, 'transaction')} {missingDate === 1 ? 'has' : 'have'} no date. The date column will be empty for {missingDate === 1 ? 'it' : 'them'}.</p>}
          {sum.taxTypesAvailable === false && <p>{VAT_UNAVAILABLE}, so every row will use {vatLabelFor(vatLabels, 3)}.</p>}
        </>
      ),
      confirmLabel: 'Download anyway',
      onConfirm: () => downloadImportFile(sum),
    });
  };

  // ── Navigation helpers ──
  const expandAll = () => {
    let rows = 0; const next = new Set(); let capped = false;
    for (const v of visibleSuppliers) {
      const n = Math.min(v.txns.length, ROW_PAGE);
      if (next.size > 0 && rows + n > EXPAND_ROW_CAP) { capped = true; break; }
      next.add(v.sup.supplierKey); rows += n;
    }
    setExpanded(next);
    if (capped) showNotice(`Expanded ${next.size} of ${visibleSuppliers.length} suppliers to keep the page fast. Use search or filters to narrow the list.`);
  };

  const goToNext = (kind) => {
    let nav = navListRef.current.filter(n => n[kind]);
    let clearedFilters = false;
    if (nav.length === 0 && filtering) {
      // The target exists but the current filters hide it: show everything and look again
      const all = [];
      for (const s of (data?.suppliers || [])) s.transactions.forEach((t, idx) => {
        const assigned = isAssigned(t, accountsById);
        const entry = { key: t.key, supKey: s.supplierKey, idx, unassigned: !assigned, review: assigned && t.assignment.status === 'suggested' };
        if (entry[kind]) all.push(entry);
      });
      nav = all; clearedFilters = all.length > 0;
    }
    if (nav.length === 0) return;
    const lastKey = navCursorRef.current[kind];
    const pos = nav.findIndex(n => n.key === lastKey);
    const target = nav[(pos + 1) % nav.length];
    navCursorRef.current[kind] = target.key;
    if (clearedFilters) { setFilters(DEFAULT_FILTERS); showNotice('Filters were cleared to show the next item.'); }
    // No explicit reveal: the focus hand-over expands the supplier / extends the row cap if the row is not rendered yet
    setPendingFocus({ keys: [target.key], target: kind === 'review' ? 'accept' : 'combobox', scroll: 'center' });
  };

  // The docked bulk bar covers the bottom of the viewport: publish its real height as --acct-bar-h so rows scroll clear of
  // it (scroll-margin-bottom, WCAG 2.4.11). Written straight to the DOM (no re-render); removed when the bar goes away.
  const barVisible = !readOnly && selected.size > 0;
  useLayoutEffect(() => {
    const root = rootRef.current; const bar = bulkRef.current;
    if (!root) return undefined;
    if (!barVisible || !bar) { root.style.removeProperty('--acct-bar-h'); return undefined; }
    const set = () => root.style.setProperty('--acct-bar-h', `${Math.ceil(bar.getBoundingClientRect().height || bar.offsetHeight || 0) + 16}px`);
    set();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(set);
      ro.observe(bar);
      return () => { ro.disconnect(); root.style.removeProperty('--acct-bar-h'); };
    }
    window.addEventListener('resize', set);
    return () => { window.removeEventListener('resize', set); root.style.removeProperty('--acct-bar-h'); };
  }, [barVisible, moreOpen, data]);

  // ── Render states ──
  if (loading && !data) {
    return (
      <div className="space-y-3" aria-busy="true">
        <p role="status" className="flex items-center gap-2 text-sm text-gray-800">
          <FiLoader className={`w-4 h-4 ${SPIN}`} aria-hidden="true" /> Loading accounting data…
        </p>
        {[0, 1, 2].map(i => (
          <div key={i} className={`${CARD} px-4 py-4 space-y-3 animate-pulse motion-reduce:animate-none`} aria-hidden="true">
            <div className="h-4 bg-gray-200 rounded w-1/3" />
            <div className="h-3 bg-gray-100 rounded w-2/3" />
            <div className="h-9 bg-gray-100 rounded w-full" />
          </div>
        ))}
      </div>
    );
  }

  if (!data) {
    return (
      <div role="alert" className="bg-red-50 border border-red-300 rounded-xl px-5 py-4 text-sm text-red-900 flex items-center justify-between gap-3 flex-wrap">
        <span className="inline-flex items-center gap-2"><FiAlertCircle aria-hidden="true" /> {error || 'Failed to load accounting data'}</span>
        <button type="button" className={BTN_SECONDARY} onClick={() => load()}>Try again</button>
      </div>
    );
  }

  if (data.noGroup) {
    return (
      <div className={`${CARD} px-5 py-8 text-center text-sm text-gray-800 space-y-2`}>
        <FiInbox className="w-8 h-8 mx-auto text-gray-600" aria-hidden="true" />
        <p className="font-medium text-gray-900">This recon is not in a group</p>
        <p>Accounts come from a recon group&apos;s Set of Accounts. Ask an administrator to add this recon to a group in Settings &gt; Supplier Recon, then reopen this tab.</p>
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div className={`${CARD} px-5 py-8 text-center text-sm text-gray-800 space-y-2`}>
        <FiBookOpen className="w-8 h-8 mx-auto text-gray-600" aria-hidden="true" />
        <p className="font-medium text-gray-900">This recon group has no Set of Accounts yet</p>
        <p>
          <Link to="/admin/settings" className={`inline-flex items-center min-h-[44px] md:min-h-0 text-blue-800 underline hover:text-blue-900 ${FOCUS_RING}`}>Open Settings &gt; Supplier Recon</Link>
          {' '}to import or add accounts, then come back here.
        </p>
      </div>
    );
  }

  const busyAny = busy || ai.running;
  const orphaned = data.orphaned || { count: 0, items: [] };
  const orphanSig = `${orphaned.count}:${(orphaned.items[0] || {}).key || ''}`;
  const showOrphans = orphaned.count > 0 && orphanDismissed !== orphanSig;
  const orphansDismissed = orphaned.count > 0 && orphanDismissed === orphanSig;
  const aiPct = ai.total > 0 ? Math.round((ai.done / ai.total) * 100) : 0;
  const selectedHidden = [...selected].filter(k => !renderedSet.has(k)).length;
  const reviewed = Math.max(reviewBase - overall.suggested, 0);

  const bulkVatControls = vatEnabled ? (
    <>
      {/* aria-busy / aria-disabled instead of disabled: a disabled control that has focus drops focus to <body> */}
      <select aria-label="Set VAT type for selected" className={`${SELECT_VAT} w-full sm:w-auto ${busyAny ? 'opacity-50 cursor-not-allowed' : ''}`} value={bulkVat}
        aria-busy={busyAny || undefined} aria-disabled={busyAny || undefined} data-bulk-vat-select=""
        onChange={(e) => { if (!busyAny) setBulkVat(e.target.value); }}>
        <option value="">Set VAT type for selected…</option>
        {Object.keys(vatLabels).map((k) => <option key={k} value={k}>{vatLabelFor(vatLabels, k)}</option>)}
        <option value="default">Use the supplier VAT type</option>
      </select>
      <button type="button" className={`${BTN_SECONDARY} ${busyAny ? 'opacity-50 cursor-not-allowed' : ''}`} disabled={!bulkVat}
        aria-busy={busyAny || undefined} aria-disabled={busyAny || undefined} data-bulk-vat-apply="" onClick={setVatSelected}>Apply VAT type</button>
    </>
  ) : null;

  const bulkSecondary = (
    <>
      <button type="button" className={BTN_DANGER} disabled={busyAny} onClick={unassignSelected}>Unassign selected</button>
      <button type="button" className={BTN_SECONDARY} onClick={clearSelection}>Clear selection</button>
    </>
  );

  return (
    <div ref={rootRef} className="space-y-4 pb-2">
      {/* Screen-reader announcements (single region; milestones only) */}
      <div className={SR_ONLY} role="status" aria-live="polite" aria-atomic="true">{announcement}</div>

      {readOnly && (
        <div className="flex items-start gap-2 bg-gray-100 border border-gray-300 rounded-lg px-4 py-3 text-xs text-gray-900">
          <FiEye className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
          <p>You have view-only access, so accounts cannot be changed. You can still search, filter and export.</p>
        </div>
      )}

      {/* Info */}
      <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-xs text-blue-900">
        <FiInfo className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
        <p>
          Each transaction takes exactly one account. Transactions are not split and VAT is not split out; the amount shown is the full transaction amount.
          Only accepted suppliers are listed. When a supplier has both purchase listing lines and invoices, only one source is counted to avoid double counting.
          The &quot;Report:&quot; label shows the payment decision made in the Report tab (read-only here).
        </p>
      </div>

      {/* Header strip */}
      <div className={`${CARD} px-5 py-4 space-y-3`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h2 ref={headingRef} tabIndex={-1} className="text-base font-semibold text-gray-900 focus:outline-none">Accounting</h2>
            <p className="text-xs text-gray-700">
              {overall.assigned} of {overall.total} transactions linked to an account
              {refreshing && <span className="ml-2 inline-flex items-center gap-1"><FiLoader className={`w-3 h-3 ${SPIN}`} aria-hidden="true" /> Refreshing…</span>}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`${CHIP} bg-gray-100 text-gray-800`}><FiCircle className="w-3 h-3" aria-hidden="true" /> Unassigned {overall.unassigned}</span>
            <span className={`${CHIP} bg-amber-100 text-amber-900`}><FiCpu className="w-3 h-3" aria-hidden="true" /> To review {overall.suggested}</span>
            <span className={`${CHIP} bg-green-100 text-green-900`}><FiCheckCircle className="w-3 h-3" aria-hidden="true" /> Confirmed {overall.confirmed}</span>
          </div>
        </div>

        <div>
          <div
            role="progressbar"
            aria-label="Transactions linked to an account"
            aria-valuemin={0}
            aria-valuemax={overall.total}
            aria-valuenow={overall.assigned}
            aria-valuetext={`${overall.confirmed} confirmed, ${overall.suggested} to review, ${overall.unassigned} unassigned`}
            className="w-full h-2.5 bg-gray-200 ring-1 ring-inset ring-gray-500 rounded-full overflow-hidden flex"
          >
            <div className="h-full bg-green-700 transition-all motion-reduce:transition-none" style={{ width: `${segConfirmed}%` }} />
            <div className="h-full bg-amber-700 transition-all motion-reduce:transition-none" style={{ width: `${segSuggested}%` }} />
          </div>
          <p className="mt-1 text-xs text-gray-800">{overall.confirmed} confirmed, {overall.suggested} to review, {overall.unassigned} unassigned</p>
        </div>

        {allDone && (
          <div className="flex items-start gap-2 bg-green-100 border border-green-700 rounded-lg px-4 py-3 text-sm text-green-900">
            <FiCheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <p>All {overall.total} transactions are linked and confirmed. You can export.</p>
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          {!readOnly && !ai.running && (
            <button ref={runBtnRef} type="button" className={allDone ? BTN_SECONDARY : BTN_PRIMARY} onClick={runAi} disabled={busyAny || overall.total === 0}
              onFocus={() => { aiFocusRef.current = 'run'; }} onBlur={(e) => { if (e.relatedTarget) aiFocusRef.current = null; }}>
              <FiZap className="w-3.5 h-3.5" aria-hidden="true" /> Run AI matching
            </button>
          )}
          {!readOnly && ai.running && (
            <div className="flex items-center gap-3 flex-wrap">
              <div className="min-w-[10rem]">
                <p className="text-xs text-gray-900 inline-flex items-center gap-2">
                  <FiLoader className={`w-4 h-4 ${SPIN} text-blue-800`} aria-hidden="true" />
                  {ai.cancelling ? 'Cancelling after the current batch…' : (ai.total > 0 ? `${ai.done} of ${ai.total} suppliers processed` : 'Starting…')}
                </p>
                <div role="progressbar" aria-label="AI matching progress" aria-valuemin={0} aria-valuemax={ai.total || 100}
                  aria-valuenow={ai.total > 0 ? ai.done : 0} className="mt-1 w-full h-1.5 bg-gray-200 ring-1 ring-inset ring-gray-500 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-700 transition-all motion-reduce:transition-none" style={{ width: `${aiPct}%` }} />
                </div>
              </div>
              <button ref={cancelBtnRef} type="button" className={BTN_SECONDARY} onClick={cancelAi} disabled={ai.cancelling}
                onFocus={() => { aiFocusRef.current = 'cancel'; }} onBlur={(e) => { if (e.relatedTarget) aiFocusRef.current = null; }}>
                <FiX className="w-3.5 h-3.5" aria-hidden="true" /> Cancel (keeps suggestions so far)
              </button>
            </div>
          )}
          {!readOnly && (
            <button type="button" className={BTN_SECONDARY} onClick={acceptAll} disabled={busyAny || overall.suggested === 0}
              aria-describedby={toolbarHintId} data-accept-all="">
              <FiCheck className="w-3.5 h-3.5" aria-hidden="true" /> {overall.suggested > 0 ? `Accept all ${overall.suggested} to review` : 'Accept all to review'}
            </button>
          )}
          {/* Below sm the secondary controls share rows (2 per row, 44px high); from sm up they flow inline as before */}
          <div className="grid grid-cols-2 gap-2 w-full sm:w-auto sm:flex sm:flex-wrap sm:items-center" data-toolbar-group="manage">
            {!readOnly && (
              <RedoMenu busy={busyAny} triggerRef={redoBtnRef} items={[
                {
                  id: 'unconfirm', label: `Unconfirm all (${overall.confirmed})`, hint: 'Confirmed transactions go back to To review',
                  disabledReason: redoBusyReason || (overall.confirmed === 0 ? 'No confirmed transactions' : null), onSelect: askUnconfirmAll,
                },
                {
                  id: 'clear', label: 'Clear everything…', hint: 'Remove all accounts and VAT choices of this recon', danger: true,
                  disabledReason: redoBusyReason || (overall.assigned === 0 && redoStats.vat === 0 && redoStats.removed === 0 && !(data?.orphaned?.count > 0) ? 'Nothing to clear' : null),
                  onSelect: askClearAll,
                },
              ]} />
            )}
            {!readOnly && (
              <button type="button" className={`${BTN_DANGER} w-full sm:w-auto`} onClick={resetSuggestions} disabled={busyAny || redoStats.resettable === 0}
                aria-describedby={toolbarHintId}>
                Reset {plural(redoStats.resettable, 'suggestion')}
              </button>
            )}
            <button type="button" className={`${BTN_SECONDARY} w-full sm:w-auto`} onClick={() => load({ silent: true })} disabled={busyAny}>
              <FiRefreshCw className={`w-3.5 h-3.5 ${refreshing ? SPIN : ''}`} aria-hidden="true" /> Refresh
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2 w-full sm:w-auto sm:flex sm:flex-wrap sm:items-center" data-toolbar-group="export">
            <button type="button" className={`${allDone ? BTN_PRIMARY : BTN_SECONDARY} w-full sm:w-auto`} onClick={exportCsv} disabled={busyAny || overall.total === 0}>
              <FiDownload className="w-3.5 h-3.5" aria-hidden="true" /> Export CSV
            </button>
            <button type="button" className={`${allDone ? BTN_PRIMARY : BTN_SECONDARY} w-full sm:w-auto ${busyAny ? 'opacity-50 cursor-not-allowed' : ''}`} onClick={exportImportFile}
              disabled={overall.total === 0} aria-disabled={busyAny || undefined} aria-busy={preparing || undefined} data-import-btn="">
              {preparing ? <FiLoader className={`w-3.5 h-3.5 ${SPIN}`} aria-hidden="true" /> : <FiFileText className="w-3.5 h-3.5" aria-hidden="true" />}
              {preparing ? 'Preparing…' : 'Accounting Import File'}
            </button>
          </div>
        </div>

        {!readOnly && (
          <p id={toolbarHintId} className="text-xs text-gray-800" data-toolbar-hint="">
            {redoStats.handSet > 0 && `Accept all includes ${redoStats.handSet} set by hand. `}Reset suggestions only removes suggestions you have not accepted. Hand-set accounts are kept.
          </p>
        )}

        {!vatEnabled && (
          <p className="text-xs text-gray-800 inline-flex items-start gap-1" data-vat-unavailable="">
            <FiInfo className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span>{VAT_UNAVAILABLE}. {VAT_UNAVAILABLE_HELP} Until then VAT types cannot be chosen and the Accounting Import File uses {vatLabelFor(vatLabels, 3)} for every row.</span>
          </p>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" className={BTN_SECONDARY} onClick={() => goToNext('unassigned')} disabled={overall.unassigned === 0}>
            <FiArrowDown className="w-3.5 h-3.5" aria-hidden="true" /> Go to next unassigned
          </button>
          <button type="button" className={BTN_SECONDARY} onClick={() => goToNext('review')} disabled={overall.suggested === 0}>
            <FiArrowDown className="w-3.5 h-3.5" aria-hidden="true" /> Go to next to review
          </button>
          {!readOnly && (
            <label className={CHECK_LABEL}>
              <input type="checkbox" className={CHECK_CLS} checked={reviewMode} onChange={(e) => setReviewMode(e.target.checked)} />
              Review mode (Accept moves to the next suggestion)
            </label>
          )}
          {!readOnly && reviewMode && reviewBase > 0 && (
            <span className="text-xs text-gray-800" data-review-progress="">Reviewed {reviewed} of {reviewBase}</span>
          )}
        </div>

        {overall.unassigned > 0 && overall.total > 0 && (
          <p className="text-xs text-amber-900 inline-flex items-center gap-1">
            <FiAlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />
            {overall.unassigned} transaction{overall.unassigned === 1 ? ' is' : 's are'} not yet linked to an account. You can still export.
          </p>
        )}
      </div>

      {/* Errors / notices stay in view while scrolling a long list (below the mobile menu button, which is fixed top-left) */}
      {(error || notice) && (
        <div className="sticky top-16 lg:top-2 z-[22] space-y-2" data-status-region="">
          {error && (
            <div role="alert" className="bg-red-50 border border-red-300 rounded-lg pl-4 pr-1 py-1 text-sm text-red-900 flex items-start justify-between gap-3 shadow-sm">
              <span className="inline-flex items-start gap-2 py-2"><FiAlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" /> {error}</span>
              <button type="button" aria-label="Dismiss error" className={`${ICON_BTN} text-red-800 hover:text-red-950`} onClick={dismissError}><FiX aria-hidden="true" /></button>
            </div>
          )}
          {notice && (
            <div role="status" className="bg-green-50 border border-green-700 rounded-lg pl-4 pr-1 py-1 text-sm text-green-900 flex items-start justify-between gap-3 shadow-sm"
              onTouchStart={() => { touchRef.current = Date.now(); setNoticeHold(false); }}
              onMouseEnter={() => { if (Date.now() - touchRef.current > 1000) setNoticeHold(true); }} // a tap emulates mouseenter and never mouseleave
              onMouseLeave={() => setNoticeHold(false)}
              onFocus={() => setNoticeHold(true)}
              onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setNoticeHold(false); }}>
              <span className="py-2">
                {notice.text}
                {notice.action && (
                  <button type="button" className={`ml-2 ${BTN_LINK}`}
                    onClick={() => { const act1 = notice.action.onClick; setNotice(null); act1(); }}>{notice.action.label}</button>
                )}
              </span>
              <button type="button" aria-label="Dismiss message" className={`${ICON_BTN} text-green-900 hover:text-green-950`} onClick={dismissNotice}><FiX aria-hidden="true" /></button>
            </div>
          )}
        </div>
      )}

      {showOrphans && (
        <div ref={bannerRef} className="bg-amber-50 border border-amber-600 rounded-lg pl-4 pr-1 py-1 text-sm text-amber-900">
          <div className="flex items-start justify-between gap-3">
            <p className="inline-flex items-start gap-2 py-2">
              <FiAlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <span>
                {orphaned.count} {orphaned.count === 1 ? 'assignment you confirmed or set by hand no longer matches' : 'assignments you confirmed or set by hand no longer match'} any transaction (an amount or reference changed). Review the affected transactions.
                {' '}
                <button type="button" data-orphan-toggle="" className={BTN_LINK} aria-expanded={orphanOpen} aria-controls={orphanOpen ? 'acct-orphans' : undefined} onClick={() => setOrphanOpen(o => !o)}>
                  {orphanOpen ? 'Hide list' : 'Show list'}
                </button>
              </span>
            </p>
            <button type="button" aria-label="Dismiss warning" className={`${ICON_BTN} text-amber-900 hover:text-amber-950`} onClick={() => dismissOrphans(orphanSig)}><FiX aria-hidden="true" /></button>
          </div>
          {orphanOpen && (
            <ul id="acct-orphans" className="pb-3 pr-3 space-y-1 text-xs">
              {orphaned.items.map(o => (
                <li key={o.key} className="flex items-center gap-2 flex-wrap bg-white border border-amber-300 rounded px-2 py-1.5">
                  <span className="font-medium break-words">{o.supplier_name}</span>
                  <span className="break-words">{o.txn_ref || 'No reference'}</span>
                  <span className="tabular-nums">{o.amount != null ? fmt(o.amount, currency) : '—'}</span>
                  <span>{o.account_code || 'No account'}</span>
                  <span className={`${CHIP} ${o.status === 'confirmed' ? 'bg-green-100 text-green-900' : 'bg-gray-100 text-gray-800'}`}>{o.status === 'confirmed' ? 'confirmed' : 'to review'}</span>
                  {!readOnly && o.relinkTo && (
                    <button type="button" data-orphan-relink={o.key} className={`${BTN_SECONDARY} !px-2`} disabled={busyAny} onClick={() => relinkOrphan(o)}>
                      <FiLink className="w-3 h-3" aria-hidden="true" /> Re-link to {o.relinkTo.amount != null ? fmt(o.relinkTo.amount, currency) : 'the current transaction'}
                    </button>
                  )}
                  <button type="button" data-orphan-go={o.key} className={`${BTN_SECONDARY} !px-2`}
                    aria-label={o.relinkTo ? `Show transaction for ${o.txn_ref || o.supplier_name}` : `Find supplier ${o.supplier_name}`} onClick={() => jumpToOrphan(o)}>
                    <FiArrowDown className="w-3 h-3" aria-hidden="true" /> {o.relinkTo ? 'Show transaction' : 'Find supplier'}
                  </button>
                </li>
              ))}
              {orphaned.count > orphaned.items.length && <li className="text-amber-900">…and {orphaned.count - orphaned.items.length} more.</li>}
            </ul>
          )}
        </div>
      )}
      {orphansDismissed && (
        <div>
          <button type="button" data-orphan-again="" className={BTN_LINK} onClick={() => setOrphanDismissed('')}>
            <FiAlertTriangle className="w-3.5 h-3.5" aria-hidden="true" /> Show warning again ({plural(orphaned.count, 'unmatched assignment')})
          </button>
        </div>
      )}

      {/* Filters: search + type, then toggles, then expand/collapse */}
      <div className="space-y-2" role="group" aria-label="Filters">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="w-full sm:w-64">
            <input type="search" aria-label="Search suppliers" placeholder="Search suppliers…" className={INPUT_CLS}
              value={filters.search} onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))} />
          </div>
          {accountTypes.length > 0 && (
            <div className="w-full sm:w-56">
              <select aria-label="Filter by account type" className={INPUT_CLS} value={filters.accountType}
                onChange={(e) => setFilters(f => ({ ...f, accountType: e.target.value }))}>
                <option value="">All account types</option>
                {accountTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}
        </div>
        <div className="flex items-center gap-x-4 gap-y-1 flex-wrap">
          <label className={CHECK_LABEL}>
            <input type="checkbox" className={CHECK_CLS} checked={filters.unassignedOnly}
              onChange={(e) => setFilters(f => ({ ...f, unassignedOnly: e.target.checked }))} />
            Unassigned only
          </label>
          <label className={CHECK_LABEL}>
            <input type="checkbox" className={CHECK_CLS} checked={filters.needsReview}
              onChange={(e) => setFilters(f => ({ ...f, needsReview: e.target.checked }))} />
            Needs review
          </label>
          <label className={CHECK_LABEL}>
            <input type="checkbox" className={CHECK_CLS} checked={unassignedFirst} onChange={(e) => setUnassignedFirst(e.target.checked)} />
            Unassigned first, then to review
          </label>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" className={BTN_SECONDARY} onClick={expandAll}>Expand all</button>
          <button type="button" className={BTN_SECONDARY} onClick={() => setExpanded(new Set())}>Collapse all</button>
          {filtering && <button type="button" className={BTN_SECONDARY} onClick={() => setFilters(DEFAULT_FILTERS)}>Clear filters</button>}
        </div>
      </div>

      {!readOnly && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 flex items-center gap-x-4 gap-y-1 flex-wrap">
          <p className="text-xs text-gray-800">Tip: click an account box on any row, type to search, then press Enter to pick.</p>
          <label className={CHECK_LABEL}>
            <input type="checkbox" className={CHECK_CLS} checked={learnRows} onChange={(e) => setLearnRows(e.target.checked)} />
            Remember my picks as supplier defaults
            <span className={`${CHIP} ${learnRows ? 'bg-blue-100 text-blue-900' : 'bg-gray-100 text-gray-800'}`}>{learnRows ? 'On' : 'Off'}</span>
          </label>
        </div>
      )}

      {/* Suppliers: a list of cards with a heading per supplier. No ancestor of any option list clips (no overflow-hidden). */}
      {data.suppliers.length === 0 ? (
        <div className={`${CARD} px-5 py-8 text-center text-sm text-gray-800 space-y-2`}>
          <FiInbox className="w-8 h-8 mx-auto text-gray-600" aria-hidden="true" />
          <p className="font-medium text-gray-900">No accepted suppliers yet</p>
          <p>Accept suppliers on the Matches tab and they will appear here.</p>
          {onOpenMatches && <button type="button" className={BTN_PRIMARY} onClick={onOpenMatches}>Go to the Matches tab</button>}
        </div>
      ) : visibleSuppliers.length === 0 ? (
        <div className={`${CARD} px-5 py-8 text-center text-sm text-gray-800 space-y-2`}>
          <FiInbox className="w-8 h-8 mx-auto text-gray-600" aria-hidden="true" />
          <p className="font-medium text-gray-900">No suppliers match the current filters</p>
          <button type="button" className={BTN_SECONDARY} onClick={() => setFilters(DEFAULT_FILTERS)}>Clear filters</button>
        </div>
      ) : (
        <ul className="space-y-3 list-none p-0 m-0" aria-label="Suppliers">
          {visibleSuppliers.map(({ sup, txns, counts }) => {
            const isOpen = expanded.has(sup.supplierKey);
            const panelId = panelIdFor(sup.supplierKey);
            const ap = applyState[sup.supplierKey] || {};
            const allVisibleKeys = txns.map(t => t.key);
            const allSelected = allVisibleKeys.length > 0 && allVisibleKeys.every(k => selected.has(k));
            const shownTotal = txns.reduce((s, t) => s + (t.amount || 0), 0);
            const fullTotal = sup.transactions.reduce((s, t) => s + (t.amount || 0), 0);
            const limit = shown[sup.supplierKey] || ROW_PAGE;
            const complete = counts.unassigned === 0 && counts.suggested === 0;
            const accent = complete ? 'border-l-green-700' : counts.suggested > 0 ? 'border-l-amber-700' : 'border-l-gray-500';
            const pctC = counts.total ? (counts.confirmed / counts.total) * 100 : 0;
            const pctS = counts.total ? (counts.suggested / counts.total) * 100 : 0;
            const scopeAll = (ap.scope || 'unassigned') === 'all';
            const overrideCount = sup.transactions.filter(t => t.taxTypeSource === 'row').length;
            return (
              <li key={sup.supplierKey} className={`${CARD} border-l-4 ${accent}`} data-supplier-card={sup.supplierKey}>
                <div className="px-4 py-3 flex items-center gap-x-3 gap-y-1 flex-wrap">
                  <h3 className="flex-1 min-w-[12rem] m-0 text-sm font-semibold text-gray-900">
                    <button type="button" aria-expanded={isOpen} aria-controls={isOpen ? panelId : undefined} data-sup-toggle={sup.supplierKey}
                      className={`w-full flex items-center gap-2 text-left min-h-[44px] md:min-h-0 rounded ${FOCUS_RING}`}
                      onClick={() => toggleExpanded(sup.supplierKey)}>
                      {isOpen ? <FiChevronDown className="w-4 h-4 text-gray-700 flex-shrink-0" aria-hidden="true" /> : <FiChevronRight className="w-4 h-4 text-gray-700 flex-shrink-0" aria-hidden="true" />}
                      <span className="break-words">{sup.supplierName}</span>
                    </button>
                  </h3>
                  <PaymentChip status={sup.paymentStatus} />
                  {counts.unassigned > 0 && <span className={`${CHIP} bg-gray-100 text-gray-800`}><FiCircle className="w-3 h-3" aria-hidden="true" /> {counts.unassigned} unassigned</span>}
                  {counts.suggested > 0 && <span className={`${CHIP} bg-amber-100 text-amber-900`}><FiCpu className="w-3 h-3" aria-hidden="true" /> {counts.suggested} to review</span>}
                  {complete && <span className={`${CHIP} bg-green-100 text-green-900`}><FiCheckCircle className="w-3 h-3" aria-hidden="true" /> Complete</span>}
                  <div className="w-full sm:w-auto flex items-center gap-3 flex-wrap text-xs text-gray-800">
                    <span>{counts.assigned} of {counts.total} linked</span>
                    <span aria-hidden="true" className="inline-block w-16 h-1.5 bg-gray-200 ring-1 ring-inset ring-gray-500 rounded-full overflow-hidden align-middle">
                      <span className="flex h-full"><span className="h-full bg-green-700" style={{ width: `${pctC}%` }} /><span className="h-full bg-amber-700" style={{ width: `${pctS}%` }} /></span>
                    </span>
                    {filtering && txns.length !== counts.total
                      ? <span className="tabular-nums">Showing {txns.length} of {counts.total}: {fmt(shownTotal, currency)}</span>
                      : <span className="tabular-nums font-medium text-gray-900">Total {fmt(fullTotal, currency)}</span>}
                    {vatEnabled && <span data-vat-supplier-text={sup.supplierKey}>VAT: {vatLabelFor(vatLabels, sup.defaultTaxType || 3)}</span>}
                  </div>
                </div>

                {isOpen && (
                  <div id={panelId} className="border-t border-gray-100 px-4 py-3 space-y-3">
                    {/* Source switch */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-gray-800" id={`${panelId}-src`}>Counted source:</span>
                      <div role="group" aria-labelledby={`${panelId}-src`} className="inline-flex rounded-lg border border-gray-500 overflow-hidden">
                        {[['pl', 'PL lines', sup.plCount], ['invoice', 'Invoices', sup.invoiceCount]].map(([val, text, n]) => (
                          <button key={val} type="button" aria-pressed={sup.source === val}
                            disabled={readOnly || busyAny || n === 0}
                            onClick={() => sup.source !== val && setSource(sup, val)}
                            className={`px-3 py-1.5 text-xs font-medium min-h-[44px] md:min-h-0 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-700 ${sup.source === val ? 'bg-blue-700 text-white' : 'bg-white text-gray-900 hover:bg-gray-50'}`}>
                            {text} ({n})
                          </button>
                        ))}
                      </div>
                      {sup.plCount > 0 && sup.invoiceCount > 0 && (
                        <span className="text-xs text-gray-700">Both exist; only the selected source is counted.</span>
                      )}
                    </div>

                    {/* Supplier VAT type (collapsed headers only show it as text) */}
                    {vatEnabled && !readOnly && (
                      <div className="flex items-center gap-x-3 gap-y-1 flex-wrap" data-vat-supplier={sup.supplierKey}>
                        <label htmlFor={`${panelId}-vat`} className="text-xs text-gray-900">VAT type (whole supplier)</label>
                        <select id={`${panelId}-vat`} className={`${SELECT_VAT} w-full sm:w-auto ${busyAny ? 'opacity-50 cursor-not-allowed' : ''}`}
                          value={String(sup.defaultTaxType || 3)} aria-busy={busyAny || undefined} aria-disabled={busyAny || undefined}
                          onChange={(e) => setSupplierVat(sup, e.target.value)}>
                          {Object.keys(vatLabels).map((k) => <option key={k} value={k}>{vatLabelFor(vatLabels, k)}</option>)}
                        </select>
                        {overrideCount > 0 && (
                          <>
                            <span className="text-xs text-gray-800" data-vat-override-hint="">{plural(overrideCount, 'row')} {overrideCount === 1 ? 'has its' : 'have their'} own VAT type.</span>
                            <button type="button" className={`${BTN_SECONDARY} ${busyAny ? 'opacity-50 cursor-not-allowed' : ''}`} data-vat-reset={sup.supplierKey}
                              aria-disabled={busyAny || undefined}
                              onClick={() => { if (!busyAny) askResetOverrides(sup, overrideCount); }}>
                              Reset {plural(overrideCount, 'row override')}
                            </button>
                          </>
                        )}
                      </div>
                    )}

                    {/* Apply to supplier */}
                    {!readOnly && (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-3 space-y-2">
                        <p className="text-xs font-medium text-gray-900">Apply one account to all of this supplier</p>
                        <div className="flex items-end gap-3 flex-wrap">
                          <div className="w-full sm:w-80">
                            <AccountCombobox accountIndex={accountIndex} value={ap.account || null} label={`Account for all of ${sup.supplierName}`}
                              rowKey={`apply-${sup.supplierKey}`} announce={announce}
                              onSelect={(a) => setApplyState(s => ({ ...s, [sup.supplierKey]: { ...ap, account: a } }))}
                              onClear={() => setApplyState(s => ({ ...s, [sup.supplierKey]: { ...ap, account: null } }))} />
                          </div>
                          <div className="flex flex-col gap-0.5 w-full sm:w-auto">
                            <label htmlFor={`${panelId}-scope`} className="text-xs text-gray-900">Apply to</label>
                            <select id={`${panelId}-scope`} className={`${SELECT_CLS} w-full sm:w-auto`} value={ap.scope || 'unassigned'}
                              onChange={(e) => setApplyState(s => ({ ...s, [sup.supplierKey]: { ...ap, scope: e.target.value } }))}>
                              <option value="unassigned">Unassigned only</option>
                              <option value="all">Replace existing accounts too</option>
                            </select>
                          </div>
                          <label className={CHECK_LABEL}>
                            <input type="checkbox" className={CHECK_CLS} checked={!!ap.learn}
                              onChange={(e) => setApplyState(s => ({ ...s, [sup.supplierKey]: { ...ap, learn: e.target.checked } }))} />
                            Remember as default for this supplier
                          </label>
                          <button type="button" className={`${BTN_PRIMARY} w-full sm:w-auto`} disabled={busyAny || !ap.account} onClick={() => applyToSupplier(sup)}>Apply</button>
                        </div>
                        {scopeAll && (
                          <p className="text-xs text-amber-900 inline-flex items-start gap-1">
                            <FiAlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" aria-hidden="true" />
                            This will overwrite {counts.assigned} existing {counts.assigned === 1 ? 'account' : 'accounts'} ({counts.confirmed} confirmed, {counts.suggested} to review).
                          </p>
                        )}
                      </div>
                    )}

                    {/* Transactions */}
                    {txns.length === 0 ? (
                      <p className="text-xs text-gray-700">No transactions to show.</p>
                    ) : (
                      <div>
                        {!readOnly && (
                          <label className={`${CHECK_LABEL} mb-1`}>
                            <input type="checkbox" className={CHECK_CLS} checked={allSelected}
                              onChange={(e) => toggleSelectMany(allVisibleKeys, e.target.checked)} />
                            Select all {txns.length} for {sup.supplierName}
                          </label>
                        )}
                        <ul className="divide-y divide-gray-100">
                          {txns.slice(0, limit).map(t => (
                            <TxnRow
                              key={t.key}
                              t={t}
                              accountIndex={accountIndex}
                              accountsById={accountsById}
                              currency={currency}
                              readOnly={readOnly}
                              selected={selected.has(t.key)}
                              pending={pendingKeys.has(t.key)}
                              rowError={rowErrors[t.key]}
                              onToggleSelect={toggleSelected}
                              onPick={onPick}
                              onClear={onClear}
                              onAccept={onAccept}
                              onDismissRowError={dismissRowError}
                              announce={announce}
                              vatEnabled={vatEnabled}
                              vatLabels={vatLabels}
                              supplierVat={sup.defaultTaxType || 3}
                              onSetVat={onSetVat}
                            />
                          ))}
                        </ul>
                        {txns.length > limit && (
                          <div className="pt-2 flex items-center gap-3 flex-wrap">
                            <span className="text-xs text-gray-800">Showing {limit} of {txns.length}</span>
                            <button type="button" className={BTN_SECONDARY}
                              onClick={() => {
                                const firstNew = txns[limit];
                                const nextLimit = limit + ROW_PAGE;
                                setShown(s => ({ ...s, [sup.supplierKey]: nextLimit }));
                                announce(`Showing ${Math.min(nextLimit, txns.length)} of ${txns.length}`);
                                if (firstNew) setPendingFocus({ keys: [firstNew.key], target: 'combobox', scroll: 'nearest' });
                              }}>
                              Show {Math.min(ROW_PAGE, txns.length - limit)} more
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <DefaultsPanel
        groupId={data.groupId || groupId}
        accountIndex={accountIndex}
        accountsById={accountsById}
        supplierNames={supplierNames}
        readOnly={readOnly}
        open={defaultsOpen}
        reloadTick={defaultsTick}
        onToggle={() => setDefaultsOpen(o => !o)}
        announce={announce}
        onError={onDefaultsError}
        askConfirm={askConfirm}
        panelRef={defaultsRef}
      />

      {/* Bulk bar: docked at the bottom (the app shell has no fixed bottom bar). It must not clip or scroll its option list, so
          there is no overflow here and the list opens upward. */}
      {!readOnly && selected.size > 0 && (
        <div ref={bulkRef} className="sticky bottom-0 z-[24] bg-white border border-blue-500 rounded-lg shadow-lg px-4 py-3 space-y-2" role="region" aria-label="Bulk assign">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs font-medium text-gray-900" data-selected-count="" aria-live="polite">
              {selected.size} selected{selectedHidden > 0 ? ` (${selectedHidden} not shown)` : ''}
            </span>
            <div className="w-full sm:w-80">
              <AccountCombobox accountIndex={accountIndex} value={bulkAccount} label="Account for selected transactions"
                rowKey="bulk" announce={announce} placement="top" onSelect={(a) => setBulkAccount(a)} onClear={() => setBulkAccount(null)} />
            </div>
            <label className={CHECK_LABEL}>
              <input type="checkbox" className={CHECK_CLS} checked={bulkLearn} onChange={(e) => setBulkLearn(e.target.checked)} />
              Remember as default
            </label>
            <button type="button" className={BTN_PRIMARY} disabled={busyAny || !bulkAccount} onClick={assignSelected}>
              Assign to selected
            </button>
            {bulkVatControls && <div className="hidden md:flex items-center gap-2" data-bulk-vat="">{bulkVatControls}</div>}
            <div className="hidden md:flex items-center gap-2">{bulkSecondary}</div>
            <button type="button" className={`${BTN_SECONDARY} md:hidden`} aria-expanded={moreOpen} onClick={() => setMoreOpen(o => !o)}>
              <FiMoreHorizontal className="w-3.5 h-3.5" aria-hidden="true" /> More
            </button>
          </div>
          {moreOpen && <div className="md:hidden flex items-center gap-2 flex-wrap">{bulkVatControls}{bulkSecondary}</div>}
        </div>
      )}

      {dlg && <ConfirmDialog dlg={dlg} onClose={() => setDlg(null)} fallbackRef={headingRef} />}
    </div>
  );
}
